import axios from "axios";
import * as Sentry from "@sentry/react";
import jwt_decode from "jwt-decode";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ConfirmationModal } from "@knowtex-ai/components";
import { useLocation, useNavigate } from "react-router-dom";
import { Events, trackEvent } from "../../lib/analytics";
import { logInfo, logWarn, permissionDenialReason } from "../../lib/sentryLogging";
import { useTutorial } from "../Tutorial/useTutorial";
import mic from "../../assets/icon _mic_.png";
import css from "./record.module.css";
import sendServerLogging from "../../utils/sendServerLogging.js";
import userJSON from "../../utils/userJSON.js";
import AlertModal from "../AlertModal";
import { useFeatureGate } from "../../hooks/useFeatureGate";
import { openPylonChat } from "../../utils/pylonChat";
import { MINIMUM_STOP_SECONDS, isRecordingTooShort } from "./recordingValidation";
import {
  AUDIO_TEST_DURATION_MS,
  CHUNK_INTERVAL_SECONDS,
  FIRST_CHUNK_GRACE_SECONDS,
  VOLUME_SILENCE_THRESHOLD,
  canStartAudioTest,
  classifyAudioChunk,
  rmsNormalizedFromTimeDomain,
  shouldDisableUploadAudio,
  shouldFailFirstChunkDeadline,
  shouldKeepPollingChunkUploads,
  shouldShowNoPatientAudioWarning,
  shouldShowSilenceWarning,
  shouldStopForInvalidChunks,
} from "./recordingEngineRules";
import { formatRecordingTime } from "./formatRecordingTime";
import { mergeTelehealthSuggestedMacros } from "../../utils/telehealthMacros";
import RecordNotePanel from "./Record/RecordNotePanel";
import RecordNoteToggle from "./Record/RecordNoteToggle";
import RecorderStage from "./Record/RecorderStage";
import RecordingErrorModal from "./Record/RecordingErrorModal";
import { countWords } from "./Record/recordLeftPaneRules";
import { useNavigationGuard } from "../../hooks/useNavigationGuard";
import {
  RecordingPictureInPicture,
  useRecordingPictureInPicture,
} from "./RecordingPictureInPicture";
import { getSessionToken } from "../../lib/sessionToken";
import {
  RECORDING_MIN_SESSION_MS,
  ensureSessionTimeLeft,
  holdSessionForRecording,
} from "../../lib/sessionExpiry";

const mimeType = "audio/mp3";
const SHARED_AUDIO_SIGNAL_THRESHOLD = 0.04;
const SHARED_AUDIO_SIGNAL_CONFIRM_MS = 300;
const MERGE_REQUEST_TIMEOUT_MS = 90000;
const DEVICE_CHANGE_DEBOUNCE_MS = 400;
const MICROPHONE_CONNECTION_TOAST_ID = "microphone-connection-status";
let chunkNumber = 0;

/**
 * @param {{
 *   patientID?: string
 *   doctorName?: string | null
 *   setIsAudio: (value: boolean) => void
 *   isAudio: boolean
 *   mrn?: string | null
 *   patientName?: string | null
 *   org?: string
 *   identifier?: string | null
 *   patient?: object | null
 *   isTelehealth?: boolean
 *   audioManager?: object | null
 *   hasTelehealthMicrophone?: boolean
 *   hasTelehealthScreenAudio?: boolean
 *   onTelehealthCleanup?: () => void
 *   isNoteAccordionOpen?: boolean
 *   setIsNoteAccordionOpen?: (open: boolean) => void
 *   hasCustomPromptHint?: boolean | null
 * }} props
 */
const AudioRecorder = ({
  patientID,
  doctorName,
  setIsAudio,
  isAudio,
  mrn,
  patientName,
  org,
  patient,
  isTelehealth,
  audioManager,
  hasTelehealthMicrophone = false,
  hasTelehealthScreenAudio = false,
  onTelehealthCleanup, // ✅ NEW PROP
  isNoteAccordionOpen,
  setIsNoteAccordionOpen,
  hasCustomPromptHint = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const vvcDotphraseGate = useFeatureGate("vvc-dotphrase");
  const hasVvcDotphraseAccess = vvcDotphraseGate.hasFeature("on");
  const floatingRecordingPopupGate = useFeatureGate("floating-recording-popup");
  const hasFloatingRecordingPopup = floatingRecordingPopupGate.hasFeature("on");
  const [permission, setPermission] = useState(false);
  const { isActive: isTutorialActive, currentStepId, nextStep, goToStep } = useTutorial();
  // Show consent message for VA organization
  const showConsentMessage = org === "VA";
  const mediaRecorder = useRef(null);
  const chunksRef = useRef([]);
  const successfulUploadCountRef = useRef(0);
  const inFlightChunkUploadsRef = useRef(0);
  const uploadAudioInFlightRef = useRef(false);
  // Release for the current recording's session hold (see holdSessionForRecording).
  // Taken out of the ref when the recorder is reset, so a recording started while the
  // previous upload is still finishing gets its own slot.
  const releaseSessionHoldRef = useRef(null);
  const takeSessionHold = () => {
    const release = releaseSessionHoldRef.current;
    releaseSessionHoldRef.current = null;
    return release ?? (() => {});
  };
  const consecutiveInvalidChunksRef = useRef(0);
  const invalidAudioIssueHandledRef = useRef(false);
  const hasAcceptedChunkRef = useRef(false);
  const firstCutIssuedRef = useRef(false);
  const firstChunkDeadlineTimeoutRef = useRef(null);
  const uploadActionsVisibleRef = useRef(false);
  const uploadSettleTimerRef = useRef(null);
  const uploadSettleGenerationRef = useRef(0);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const dataArrayRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const isMonitoringRef = useRef(false);
  const silenceGainNodeRef = useRef(null);
  const streamRef = useRef(null);
  const lastSoundDetectedRef = useRef(Date.now());
  const recordingStatusRef = useRef("inactive");
  const latestIsTelehealthRef = useRef(isTelehealth);
  const latestAudioManagerRef = useRef(audioManager);
  const micDeviceChangedWhilePausedRef = useRef(false);
  const resumeRecoveryInProgressRef = useRef(false);
  const micReacquireTimeoutRef = useRef(null);
  const isUnmountedRef = useRef(false);
  const audioTestActiveRef = useRef(false);
  const audioTestStartingRef = useRef(false);
  const audioTestRefreshGenerationRef = useRef(0);
  const audioTestTimeoutRef = useRef(null);
  const micSwapGenerationRef = useRef(0);
  const deviceChangeDebounceRef = useRef(null);

  const [recordingStatus, setRecordingStatus] = useState("inactive");
  const [stream, setStream] = useState(null);
  const [audio, setAudio] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [audioURLs, setAudioURLs] = useState([]);
  const [readyToUpload, setReadyToUpload] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [showUploadBanner, setShowUploadBanner] = useState(false);
  const [isStartingRecording, setIsStartingRecording] = useState(false);

  const [conversationID, setConversationID] = useState(null);
  const conversationIDRef = useRef(null);

  const [hasRecorded, setHasRecorded] = useState(false);

  const [diasbleUpload, setDisableUpload] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [telehealthMicVolumeLevel, setTelehealthMicVolumeLevel] = useState(0);
  const [telehealthScreenVolumeLevel, setTelehealthScreenVolumeLevel] = useState(0);
  const [showNoAudioWarning, setShowNoAudioWarning] = useState(false);
  const [availableMicrophones, setAvailableMicrophones] = useState([]);
  const [selectedMicId, setSelectedMicId] = useState("");
  const selectedMicIdRef = useRef("");
  const preferredMicRef = useRef(null);
  const [activeMicId, setActiveMicId] = useState("");
  const activeMicIdRef = useRef("");
  const [isUsingFallbackMic, setIsUsingFallbackMic] = useState(false);
  const microphoneToastStateRef = useRef("normal");
  const [isAudioTestActive, setIsAudioTestActive] = useState(false);
  const [isAudioTestStarting, setIsAudioTestStarting] = useState(false);
  const activeMicrophoneLabel = useMemo(
    () =>
      availableMicrophones.find((device) => device.deviceId === activeMicId)?.label ||
      "Available microphone",
    [activeMicId, availableMicrophones]
  );
  const preferredMicrophoneLabel = useMemo(
    () =>
      availableMicrophones.find((device) => device.deviceId === selectedMicId)?.label ||
      preferredMicRef.current?.label ||
      "preferred microphone",
    [availableMicrophones, selectedMicId]
  );
  useEffect(() => {
    const previousState = microphoneToastStateRef.current;

    if (isUsingFallbackMic) {
      if (previousState !== "fallback") {
        toast.warning(
          `${activeMicrophoneLabel} is now in use. Knowtex will automatically reconnect to ${preferredMicrophoneLabel} when it becomes available.`,
          {
            toastId: MICROPHONE_CONNECTION_TOAST_ID,
            role: "status",
            autoClose: 8000,
            closeOnClick: false,
            pauseOnFocusLoss: true,
            pauseOnHover: true,
          }
        );
      }
      microphoneToastStateRef.current = "fallback";
      return;
    }

    if (previousState === "fallback") {
      toast.dismiss(MICROPHONE_CONNECTION_TOAST_ID);
      toast.success(`Reconnected to ${preferredMicrophoneLabel}.`, {
        toastId: MICROPHONE_CONNECTION_TOAST_ID,
        role: "status",
        autoClose: 5000,
        closeOnClick: false,
        pauseOnFocusLoss: true,
        pauseOnHover: true,
      });
    }
    microphoneToastStateRef.current = "normal";
  }, [activeMicrophoneLabel, isUsingFallbackMic, preferredMicrophoneLabel]);
  useEffect(
    () => () => {
      toast.dismiss(MICROPHONE_CONNECTION_TOAST_ID);
    },
    []
  );
  const telehealthLevelMonitoringRef = useRef(false);
  const telehealthLevelAnimationRef = useRef(null);
  const telehealthMicSourceNodeRef = useRef(null);
  const telehealthScreenSourceNodeRef = useRef(null);
  const telehealthMicAnalyserRef = useRef(null);
  const telehealthScreenAnalyserRef = useRef(null);
  const telehealthMicDataArrayRef = useRef(null);
  const telehealthScreenDataArrayRef = useRef(null);
  const telehealthScreenTrackRef = useRef(null);
  const telehealthLevelMuteGainRef = useRef(null);
  const telehealthMicInstantLevelRef = useRef(0);
  const sharedAudioSignalStartedAtRef = useRef(null);
  const sharedAudioDetectedTrackRef = useRef(null);
  const [hasDetectedSharedAudio, setHasDetectedSharedAudio] = useState(false);
  const [telehealthAudioRevision, setTelehealthAudioRevision] = useState(0);
  const [noteContent, setNoteContent] = useState("");
  const [isNoteLoading, setIsNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState(null);
  const [hasFetchedNote, setHasFetchedNote] = useState(false);
  const [noteAvailable, setNoteAvailable] = useState(false);
  const [patientStatus, setPatientStatus] = useState("");
  const [activeLeftPaneTab, setActiveLeftPaneTab] = useState("note");
  const [chartHistory, setChartHistory] = useState(null);
  const [isChartHistoryLoading, setIsChartHistoryLoading] = useState(false);
  const [chartHistoryError, setChartHistoryError] = useState(null);
  const [customPromptValue, setCustomPromptValue] = useState("");
  const [hasCustomPrompt, setHasCustomPrompt] = useState(hasCustomPromptHint === true);
  const [hasFetchedCustomPrompt, setHasFetchedCustomPrompt] = useState(
    hasCustomPromptHint === false
  );
  const [isCustomPromptLoading, setIsCustomPromptLoading] = useState(false);
  const [isCustomPromptExpanded, setIsCustomPromptExpanded] = useState(false);
  const customPromptFetchInFlightRef = useRef(false);

  const persistTelehealthSuggestedMacros = useCallback(async () => {
    if (!patientID || !isTelehealth || !hasVvcDotphraseAccess) {
      return;
    }

    const nextMacros = mergeTelehealthSuggestedMacros(patient?.macros);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/todos/${patientID}/macros`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
          body: JSON.stringify({ macros: nextMacros }),
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.message || "Unable to update telehealth dot phrases.");
      }
    } catch (error) {
      console.error("Failed to persist telehealth dot phrases:", error);
      Sentry.captureException(error, {
        tags: { flow: "persistTelehealthSuggestedMacros" },
        extra: { patientId: patientID },
      });
    }
  }, [patientID, isTelehealth, hasVvcDotphraseAccess, patient?.macros]);

  //timer
  const [elapsedTime, setElapsedTime] = useState(0); // Recording duration in seconds

  const [showRecordingErrorModal, setShowRecordingErrorModal] = useState(false);
  const [alertModal, setAlertModal] = useState(null);
  const [showDeleteAudioModal, setShowDeleteAudioModal] = useState(false);
  const noteAccordionRef = useRef(null);
  const {
    close: closeFloatingRecordingIndicator,
    isOpen: isFloatingRecordingIndicatorOpen,
    isOpening: isFloatingRecordingIndicatorOpening,
    isSupported: isFloatingRecordingIndicatorSupported,
    open: openFloatingRecordingIndicator,
    pictureInPictureWindow,
  } = useRecordingPictureInPicture();

  useEffect(() => {
    if (!isNoteAccordionOpen) return;
    const timer = setTimeout(() => {
      const panel = noteAccordionRef.current;
      if (!panel) return;
      const firstFocusable = panel.querySelector(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }, 450);
    return () => clearTimeout(timer);
  }, [isNoteAccordionOpen]);

  // Check if this is a mock patient (ONLY for "QQQVAINTERFACE,IFC")
  const isMockPatient = useMemo(() => {
    return patientName && patientName === "QQQVAINTERFACE,IFC" && isTutorialActive;
  }, [patientName, isTutorialActive]);

  const hasPendingRecording = useMemo(
    () =>
      recordingStatus === "recording" ||
      recordingStatus === "paused" ||
      readyToUpload ||
      (hasRecorded && !isUploaded),
    [recordingStatus, readyToUpload, hasRecorded, isUploaded]
  );

  const {
    showConfirmation: showLeaveConfirmation,
    confirmNavigation: confirmLeave,
    cancelNavigation: cancelLeave,
  } = useNavigationGuard(hasPendingRecording);

  const handleReturnHomeClick = async () => {
    // For mock patients, update note and status when returning home (but don't block navigation)
    if (isMockPatient && isUploaded) {
      // Navigate immediately, don't wait for API calls
      navigate("/dashboard");

      // Make API call to generate full mock note with all associated data
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("token", getSessionToken());

      const updateBody = {
        patient_id: patientID,
      };

      fetch(`${process.env.REACT_APP_API_URL}/notes/mock-note`, {
        method: "PUT",
        headers: myHeaders,
        body: JSON.stringify(updateBody),
      })
        .then((response) => {
          if (!response.ok) {
            return response.json().then((err) => {
              throw new Error(err.error || `Failed to update patient: ${response.statusText}`);
            });
          }
          return response.json();
        })
        .then((data) => {
          // Server logging
          sendServerLogging({
            body: {
              ...userJSON(localStorage.getItem("userName")),
              event: "MOCK_NOTE_UPDATED_ON_RETURN_HOME",
              eventData: { mrn: mrn, patientName: patientName },
              patientId: patientID,
            },
          });
        })
        .catch((error) => {
          Sentry.captureException(error, {
            tags: { logEvent: "ERROR_MOCK_UPDATE_ON_RETURN_HOME" },
            extra: { patientId: patientID },
          });
          // Server logging
          sendServerLogging({
            body: {
              ...userJSON(localStorage.getItem("userName")),
              event: "ERROR_MOCK_UPDATE_ON_RETURN_HOME",
              eventData: { error_message: error.message.toString() },
              patientId: patientID,
            },
          });
        });

      return; // Return early for mock patients to prevent double navigation
    }

    // For non-mock patients, navigate normally
    navigate("/dashboard");
  };

  useEffect(() => {
    setIsNoteAccordionOpen(false);
    setNoteContent("");
    setNoteError(null);
    setHasFetchedNote(false);
    setNoteAvailable(false);
    setPatientStatus("");
    setActiveLeftPaneTab("note");
    setChartHistory(null);
    setChartHistoryError(null);
    setIsChartHistoryLoading(false);
    setCustomPromptValue("");
    setHasCustomPrompt(hasCustomPromptHint === true);
    setHasFetchedCustomPrompt(hasCustomPromptHint === false);
    setIsCustomPromptLoading(false);
    setIsCustomPromptExpanded(false);
    customPromptFetchInFlightRef.current = false;
  }, [patientID, hasCustomPromptHint]);

  const fetchPatientNote = useCallback(async () => {
    if (!patientID) {
      return;
    }

    setIsNoteLoading(true);
    setNoteError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/todos/${patientID}/updated`,
        {
          method: "GET",
          headers: { token: getSessionToken() },
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const message = errorBody.message || "Unable to load note for this patient.";
        throw new Error(message);
      }

      const data = await response.json();
      const rawNote = data.note;
      const retrievedNote = typeof rawNote === "string" ? rawNote : "";
      const statusValue = typeof data.status === "string" ? data.status : "";
      const hasNoteContent = retrievedNote.trim().length > 0;

      setNoteContent(retrievedNote);
      setPatientStatus(statusValue);
      setNoteAvailable(hasNoteContent);
      setHasFetchedNote(true);

      let hasPrechartFlag = false;
      if (chartHistory) {
        let parsedChartHistory = chartHistory;
        if (typeof chartHistory === "string") {
          try {
            parsedChartHistory = JSON.parse(chartHistory);
          } catch (error) {
            parsedChartHistory = null;
          }
        }
        hasPrechartFlag = Boolean(parsedChartHistory?.is_prechart);
      }

      if (!hasNoteContent && statusValue !== "Processing Audio" && !hasPrechartFlag) {
        setIsNoteAccordionOpen(false);
      }
    } catch (err) {
      console.error("Failed to load patient note:", err);
      Sentry.captureException(err, {
        tags: { flow: "fetchPatientNote" },
        extra: { patientId: patientID },
      });
      setNoteError(err.message || "Unable to load note for this patient.");
      setNoteAvailable(false);
      let hasPrechartFlag = false;
      if (chartHistory) {
        let parsedChartHistory = chartHistory;
        if (typeof chartHistory === "string") {
          try {
            parsedChartHistory = JSON.parse(chartHistory);
          } catch (error) {
            parsedChartHistory = null;
          }
        }
        hasPrechartFlag = Boolean(parsedChartHistory?.is_prechart);
      }
      if (!hasPrechartFlag) {
        setIsNoteAccordionOpen(false);
      }
      setPatientStatus("");
    } finally {
      setIsNoteLoading(false);
    }
  }, [patientID, chartHistory]);

  useEffect(() => {
    if (!patientID) {
      return;
    }

    fetchPatientNote();
  }, [patientID, fetchPatientNote]);

  const handleToggleNoteAccordion = () => {
    const nextState = !isNoteAccordionOpen;

    if (nextState) {
      const preferredTab = noteAvailable ? "note" : isPrecharting ? "chart_history" : "note";
      setActiveLeftPaneTab(preferredTab);
      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "VIEW_PRECHART_NOTE",
          eventData: { mrn: mrn },
          patientId: patientID,
        },
      });
    }

    setIsNoteAccordionOpen(nextState);
    fetchPatientNote();
  };

  const fetchChartHistory = useCallback(async () => {
    if (!patientID) {
      return;
    }

    setIsChartHistoryLoading(true);
    setChartHistoryError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/todos/${patientID}/chart-history`,
        {
          method: "GET",
          headers: { token: getSessionToken() },
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const message = errorBody.message || "Unable to load chart history for this patient.";
        throw new Error(message);
      }

      const data = await response.json();
      setChartHistory(data?.chartHistory ?? data?.chart_history ?? data ?? null);
    } catch (error) {
      console.error("Failed to load chart history:", error);
      setChartHistoryError(error.message || "Unable to load chart history for this patient.");
      setChartHistory(null);
    } finally {
      setIsChartHistoryLoading(false);
    }
  }, [patientID]);

  useEffect(() => {
    if (!patientID) {
      setChartHistory(null);
      return;
    }

    fetchChartHistory();
  }, [patientID, fetchChartHistory]);

  const fetchCustomPromptSilently = useCallback(
    async ({ force = false } = {}) => {
      if (!patientID) {
        return "";
      }

      if (hasCustomPromptHint === false && !force) {
        setHasCustomPrompt(false);
        setHasFetchedCustomPrompt(true);
        return "";
      }

      if (!force && hasFetchedCustomPrompt) {
        return customPromptValue;
      }

      if (customPromptFetchInFlightRef.current) {
        return customPromptValue;
      }

      customPromptFetchInFlightRef.current = true;
      setIsCustomPromptLoading(true);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/notes/todos/${patientID}/smart-chart-state`,
          {
            method: "GET",
            headers: { token: getSessionToken() },
          }
        );

        if (!response.ok) {
          setCustomPromptValue("");
          setHasCustomPrompt(false);
          return "";
        }

        const data = await response.json().catch(() => ({}));
        const nextCustomPrompt = typeof data?.custom_prompt === "string" ? data.custom_prompt : "";
        setCustomPromptValue(nextCustomPrompt);
        setHasCustomPrompt(nextCustomPrompt.trim().length > 0);
        return nextCustomPrompt;
      } catch {
        setCustomPromptValue("");
        setHasCustomPrompt(false);
        return "";
      } finally {
        setHasFetchedCustomPrompt(true);
        setIsCustomPromptLoading(false);
        customPromptFetchInFlightRef.current = false;
      }
    },
    [patientID, hasCustomPromptHint, hasFetchedCustomPrompt, customPromptValue]
  );

  useEffect(() => {
    if (!isNoteAccordionOpen) {
      return;
    }

    if (activeLeftPaneTab !== "chart_history") {
      return;
    }

    if (hasCustomPromptHint === false || hasFetchedCustomPrompt) {
      return;
    }

    fetchCustomPromptSilently();
  }, [
    isNoteAccordionOpen,
    activeLeftPaneTab,
    hasCustomPromptHint,
    hasFetchedCustomPrompt,
    fetchCustomPromptSilently,
  ]);

  const handleToggleCustomPrompt = useCallback(async () => {
    if (isCustomPromptExpanded) {
      setIsCustomPromptExpanded(false);
      return;
    }

    const nextCustomPrompt = await fetchCustomPromptSilently({
      force: !hasFetchedCustomPrompt,
    });

    if (typeof nextCustomPrompt === "string" && nextCustomPrompt.trim().length > 0) {
      setIsCustomPromptExpanded(true);
    }
  }, [fetchCustomPromptSilently, hasFetchedCustomPrompt, isCustomPromptExpanded]);

  const notePlaceholder =
    patientStatus === "Processing Audio"
      ? "This note will be updated once the current audio has been processed."
      : "No note saved for this patient yet.";

  const normalizedChartHistory = useMemo(() => {
    const sourceChartHistory = chartHistory;
    if (!sourceChartHistory) {
      return null;
    }

    if (typeof sourceChartHistory === "string") {
      try {
        return JSON.parse(sourceChartHistory);
      } catch (error) {
        console.warn("Failed to parse chart history:", error);
        return null;
      }
    }

    return sourceChartHistory;
  }, [chartHistory]);
  const isPrecharting = Boolean(
    normalizedChartHistory &&
    typeof normalizedChartHistory === "object" &&
    normalizedChartHistory?.is_prechart
  );

  const showNoteButton = noteAvailable || isPrecharting;

  const chartHistoryFields = useMemo(() => {
    if (!normalizedChartHistory || typeof normalizedChartHistory !== "object") {
      return [];
    }

    const fields = [
      { key: "assessment", label: "Assessment" },
      { key: "plan", label: "Plan" },
      {
        key: "past_medical_history",
        label: "Active Problems",
        aliases: ["problems"],
      },
      { key: "past_surgical_history", label: "Past Surgical History" },
      { key: "family_history", label: "Family History" },
      { key: "social_history", label: "Social History" },
      { key: "allergies", label: "Allergies" },
      { key: "medications", label: "Medications" },
      { key: "vitals", label: "Vitals" },
      { key: "labs", label: "Labs", aliases: ["lab_results"] },
      { key: "patient_age", label: "Age" },
      { key: "patient_dob", label: "Date of Birth" },
      { key: "gender", label: "Gender" },
    ];

    return fields
      .map((field) => {
        const candidateKeys = [field.key, ...(field.aliases || [])];
        let rawValue;
        for (const candidateKey of candidateKeys) {
          const nextValue = normalizedChartHistory[candidateKey];
          if (nextValue !== null && nextValue !== undefined) {
            const normalized =
              typeof nextValue === "string" ? nextValue.trim() : String(nextValue).trim();
            if (normalized.length > 0) {
              rawValue = nextValue;
              break;
            }
          }
        }
        if (rawValue === null || rawValue === undefined) {
          return null;
        }

        const value = typeof rawValue === "string" ? rawValue.trim() : String(rawValue);
        if (!value) {
          return null;
        }

        return { ...field, value };
      })
      .filter(Boolean);
  }, [normalizedChartHistory]);

  useEffect(() => {
    isUnmountedRef.current = false;

    // Try to get microphone permission immediately upon mounting
    getMicrophonePermission();

    // Cleanup function to stop the stream when unmounting
    return () => {
      isUnmountedRef.current = true;
      if (micReacquireTimeoutRef.current) {
        clearTimeout(micReacquireTimeoutRef.current);
        micReacquireTimeoutRef.current = null;
      }
      if (deviceChangeDebounceRef.current) {
        clearTimeout(deviceChangeDebounceRef.current);
        deviceChangeDebounceRef.current = null;
      }
      micSwapGenerationRef.current += 1;
      stopVolumeMonitoring(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    recordingStatusRef.current = recordingStatus;
  }, [recordingStatus]);

  useEffect(() => {
    latestIsTelehealthRef.current = isTelehealth;
    latestAudioManagerRef.current = audioManager;
  }, [audioManager, isTelehealth]);

  useEffect(() => {
    if (!hasFloatingRecordingPopup || (recordingStatus === "inactive" && !isStartingRecording)) {
      closeFloatingRecordingIndicator();
    }
  }, [
    closeFloatingRecordingIndicator,
    hasFloatingRecordingPopup,
    isStartingRecording,
    recordingStatus,
  ]);

  useEffect(() => {
    selectedMicIdRef.current = selectedMicId;
  }, [selectedMicId]);

  useEffect(() => {
    activeMicIdRef.current = activeMicId;
  }, [activeMicId]);

  const getMicrophonePermission = async (
    deviceId = null,
    {
      updatePreference = Boolean(deviceId),
      allowFallback = true,
      reason = "permission",
      useSelectedDevice = true,
    } = {}
  ) => {
    if ("MediaRecorder" in window) {
      const swapGeneration = ++micSwapGenerationRef.current;
      try {
        // ✅ Telehealth branch - check if sources are ready
        if (isTelehealth && audioManager) {
          const combinedStream = audioManager.getCombinedStream();

          if (!combinedStream) {
            console.warn("⚠️ Telehealth audio sources not ready yet");
            setPermission(false);
            setStream(null);
            return;
          }

          if (streamRef.current && streamRef.current !== combinedStream) {
            streamRef.current.getTracks().forEach((track) => track.stop());
          }
          streamRef.current = combinedStream;
          setPermission(true);
          setStream(combinedStream);
          return;
        }

        // ✅ Regular microphone mode (or telehealth was just cleaned up)
        const resolvedDeviceId =
          deviceId ||
          (useSelectedDevice ? selectedMicIdRef.current || selectedMicId || null : null);
        let mediaStream = null;
        let usedFallback = false;
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: resolvedDeviceId ? { deviceId: { exact: resolvedDeviceId } } : true,
            video: false,
          });
        } catch (micSelectionError) {
          const shouldFallbackToDefault = Boolean(resolvedDeviceId) && allowFallback;

          if (!shouldFallbackToDefault) {
            throw micSelectionError;
          }

          console.warn(
            "Selected microphone unavailable, falling back to system default microphone.",
            micSelectionError
          );
          mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
          });
          usedFallback = true;
        }

        const track = mediaStream.getAudioTracks()[0];
        if (!track || track.readyState !== "live") {
          mediaStream.getTracks().forEach((candidateTrack) => candidateTrack.stop());
          throw new Error("The selected microphone did not provide a live audio track.");
        }
        if (swapGeneration !== micSwapGenerationRef.current || isUnmountedRef.current) {
          mediaStream.getTracks().forEach((candidateTrack) => candidateTrack.stop());
          return null;
        }

        const previousStream = streamRef.current;
        if (recordingDestinationRef.current || audioTestActiveRef.current) {
          await connectAudioSource(mediaStream);
        }
        streamRef.current = mediaStream;
        setPermission(true);
        setStream(mediaStream);
        const activeDeviceId = track?.getSettings?.()?.deviceId || resolvedDeviceId || "";
        activeMicIdRef.current = activeDeviceId;
        setActiveMicId(activeDeviceId);

        if ((updatePreference && !usedFallback) || !preferredMicRef.current) {
          const requestedDevice = availableMicrophones.find(
            (device) => device.deviceId === resolvedDeviceId
          );
          preferredMicRef.current = {
            deviceId: usedFallback ? resolvedDeviceId || activeDeviceId : activeDeviceId,
            label: usedFallback ? requestedDevice?.label || "" : track.label || "",
            groupId: usedFallback
              ? requestedDevice?.groupId || ""
              : track?.getSettings?.()?.groupId || "",
          };
          setSelectedMicId(activeDeviceId);
        }
        const preferredId = preferredMicRef.current?.deviceId || "";
        const usingFallback = Boolean(preferredId && activeDeviceId !== preferredId);
        setIsUsingFallbackMic(usingFallback);

        track.onended = () => {
          if (streamRef.current !== mediaStream || isUnmountedRef.current) {
            return;
          }
          void reconcileRegularMicrophone("track-ended");
        };

        if (!recordingDestinationRef.current && previousStream && previousStream !== mediaStream) {
          previousStream.getTracks().forEach((previousTrack) => previousTrack.stop());
        }
        if (track) {
          console.log("🎤 Microphone stream ready:", {
            deviceId: activeDeviceId || "default",
            label: track.label,
            readyState: track.readyState,
            reason,
          });
        }
        return mediaStream;
      } catch (err) {
        console.error("❌ Error getting microphone permission:", err);
        Sentry.captureException(err, {
          tags: { flow: "getMicrophonePermission" },
          extra: { patientId: patientID },
        });

        const existingTrack = streamRef.current?.getAudioTracks?.()[0];
        if (!existingTrack || existingTrack.readyState !== "live") {
          setPermission(false);
          setStream(null);
        }

        sendServerLogging({
          body: {
            ...userJSON(localStorage.getItem("userName")),
            event: isTelehealth ? "ERROR_TELEHEALTH_PERMISSION" : "ERROR_MICROPHONE_PERMISSION",
            eventData: { error_message: err.message.toString() },
            patientId: patientID,
          },
        });
        if (
          !isMockPatient &&
          (err?.name === "NotAllowedError" || err?.name === "PermissionDeniedError")
        ) {
          logInfo("Recording permission denied", "recording.permission_denied", {
            patient_id: patientID,
            source: "microphone",
            is_telehealth: isTelehealth,
            error_name: err.name,
            denial_reason: permissionDenialReason(err),
          });
        }

        alert(err.message);
        return null;
      }
    } else {
      alert("The MediaRecorder API is not supported in your browser.");
    }
  };

  useEffect(() => {
    const loadMicrophones = async () => {
      if (!navigator.mediaDevices?.enumerateDevices) {
        return;
      }

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const microphones = devices.filter((device) => device.kind === "audioinput");
        setAvailableMicrophones(microphones);
        setSelectedMicId((current) => {
          if (current && microphones.some((device) => device.deviceId === current)) {
            return current;
          }
          if (current && preferredMicRef.current?.deviceId === current) {
            return current;
          }
          return microphones[0]?.deviceId || "";
        });
      } catch (err) {
        console.warn("Failed to enumerate microphones", err);
      }
    };

    loadMicrophones();

    if (!navigator.mediaDevices?.addEventListener) {
      return undefined;
    }

    navigator.mediaDevices.addEventListener("devicechange", loadMicrophones);
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", loadMicrophones);
    };
  }, []);

  const handleMicrophoneSelect = async (event) => {
    const nextDeviceId = event.target.value;
    const previousPreferred = preferredMicRef.current;
    const previousSelectedMicId = selectedMicIdRef.current;
    const selectedDevice = availableMicrophones.find((device) => device.deviceId === nextDeviceId);
    preferredMicRef.current = {
      deviceId: nextDeviceId,
      label: selectedDevice?.label || "",
      groupId: selectedDevice?.groupId || "",
    };
    setSelectedMicId(nextDeviceId);
    console.log("🎚️ Switching microphone:", nextDeviceId);
    const currentStatus = recordingStatusRef.current;
    const shouldReconnectProxy = currentStatus === "recording" || currentStatus === "paused";

    if (isTelehealth && audioManager) {
      const result = await audioManager.setupMicrophone(nextDeviceId);
      if (result.success) {
        const managerState = audioManager.getState();
        activeMicIdRef.current = managerState.activeMicrophoneId || nextDeviceId;
        setActiveMicId(managerState.activeMicrophoneId || nextDeviceId);
        setIsUsingFallbackMic(false);
        await updateTelehealthAudioSource();
        if (shouldReconnectProxy && streamRef.current) {
          await connectAudioSource(streamRef.current);
        }
        if (currentStatus === "recording" && streamRef.current) {
          await startVolumeMonitoring(streamRef.current);
        }
        micDeviceChangedWhilePausedRef.current = false;
      } else {
        preferredMicRef.current = previousPreferred;
        setSelectedMicId(previousSelectedMicId);
      }
      return;
    }

    const nextStream = await getMicrophonePermission(nextDeviceId, {
      updatePreference: true,
      allowFallback: false,
      reason: "manual-selection",
    });
    if (!nextStream) {
      preferredMicRef.current = previousPreferred;
      setSelectedMicId(previousSelectedMicId);
      return;
    }
    if (shouldReconnectProxy && streamRef.current) {
      await connectAudioSource(streamRef.current);
    }
    if (currentStatus === "recording" && streamRef.current) {
      await startVolumeMonitoring(streamRef.current);
    }
    if (shouldReconnectProxy) {
      micDeviceChangedWhilePausedRef.current = false;
    }
  };

  const getRmsNormalizedLevel = (analyserNode, targetArray) => {
    if (!analyserNode || !targetArray) {
      return 0;
    }

    // A newly allocated Uint8Array contains zeroes, which represent maximum
    // negative amplitude—not silence. Pre-fill with Web Audio's silence value
    // so a suspended/no-op analyser cannot produce a false-positive level.
    targetArray.fill(128);
    try {
      analyserNode.getByteTimeDomainData(targetArray);
    } catch {
      return 0;
    }
    return rmsNormalizedFromTimeDomain(targetArray);
  };

  const stopTelehealthLevelMonitoring = (shouldResetLevels = true) => {
    telehealthLevelMonitoringRef.current = false;

    if (telehealthLevelAnimationRef.current) {
      cancelAnimationFrame(telehealthLevelAnimationRef.current);
      telehealthLevelAnimationRef.current = null;
    }

    const safelyDisconnect = (nodeRef) => {
      if (!nodeRef.current) {
        return;
      }
      try {
        nodeRef.current.disconnect();
      } catch (error) {
        console.warn("Failed to disconnect telehealth level node", error);
      }
      nodeRef.current = null;
    };

    safelyDisconnect(telehealthMicSourceNodeRef);
    safelyDisconnect(telehealthScreenSourceNodeRef);
    safelyDisconnect(telehealthMicAnalyserRef);
    safelyDisconnect(telehealthScreenAnalyserRef);
    safelyDisconnect(telehealthLevelMuteGainRef);

    telehealthMicDataArrayRef.current = null;
    telehealthScreenDataArrayRef.current = null;
    telehealthScreenTrackRef.current = null;
    telehealthMicInstantLevelRef.current = 0;
    sharedAudioSignalStartedAtRef.current = null;

    if (shouldResetLevels) {
      setTelehealthMicVolumeLevel(0);
      setTelehealthScreenVolumeLevel(0);
    }
  };

  const startTelehealthLevelMonitoring = async ({
    monitorMicrophone = true,
    monitorScreen = true,
  } = {}) => {
    if (!isTelehealth || !audioManager || !audioContextRef.current) {
      stopTelehealthLevelMonitoring();
      return;
    }

    const micStream = monitorMicrophone
      ? (typeof audioManager.getMicrophoneStream === "function"
          ? audioManager.getMicrophoneStream()
          : audioManager.microphone) || null
      : null;
    const screenStream = monitorScreen
      ? (typeof audioManager.getScreenAudioStream === "function"
          ? audioManager.getScreenAudioStream()
          : audioManager.screenAudio) || null
      : null;
    const screenTrack = screenStream?.getAudioTracks?.()[0] || null;

    // Keep detection across mic swaps; only a different shared tab needs re-checking.
    if (screenTrack?.readyState !== "live" || screenTrack !== sharedAudioDetectedTrackRef.current) {
      sharedAudioDetectedTrackRef.current = null;
      setHasDetectedSharedAudio(false);
    }

    stopTelehealthLevelMonitoring(false);
    setTelehealthMicVolumeLevel(0);
    setTelehealthScreenVolumeLevel(0);

    if (!micStream && !screenStream) {
      return;
    }

    telehealthLevelMonitoringRef.current = true;

    const muteGain = audioContextRef.current.createGain();
    muteGain.gain.value = 0;
    muteGain.connect(audioContextRef.current.destination);
    telehealthLevelMuteGainRef.current = muteGain;

    if (micStream && micStream.getAudioTracks().length > 0) {
      const micAnalyser = audioContextRef.current.createAnalyser();
      micAnalyser.fftSize = 256;
      const micSource = audioContextRef.current.createMediaStreamSource(micStream);
      micSource.connect(micAnalyser);
      micAnalyser.connect(muteGain);
      telehealthMicSourceNodeRef.current = micSource;
      telehealthMicAnalyserRef.current = micAnalyser;
      telehealthMicDataArrayRef.current = new Uint8Array(micAnalyser.frequencyBinCount);
    }

    if (screenTrack && screenTrack.readyState === "live") {
      const screenAnalyser = audioContextRef.current.createAnalyser();
      screenAnalyser.fftSize = 256;
      const screenSource = audioContextRef.current.createMediaStreamSource(screenStream);
      screenSource.connect(screenAnalyser);
      screenAnalyser.connect(muteGain);
      telehealthScreenSourceNodeRef.current = screenSource;
      telehealthScreenAnalyserRef.current = screenAnalyser;
      telehealthScreenDataArrayRef.current = new Uint8Array(screenAnalyser.frequencyBinCount);
      telehealthScreenTrackRef.current = screenTrack;
    }

    const tick = () => {
      if (!telehealthLevelMonitoringRef.current) {
        return;
      }

      const micLevel = getRmsNormalizedLevel(
        telehealthMicAnalyserRef.current,
        telehealthMicDataArrayRef.current
      );
      const screenLevel = getRmsNormalizedLevel(
        telehealthScreenAnalyserRef.current,
        telehealthScreenDataArrayRef.current
      );

      telehealthMicInstantLevelRef.current = micLevel;
      setTelehealthMicVolumeLevel((prev) => prev * 0.65 + micLevel * 0.35);
      setTelehealthScreenVolumeLevel((prev) => prev * 0.65 + screenLevel * 0.35);

      if (!sharedAudioDetectedTrackRef.current) {
        const activeScreenTrack = telehealthScreenTrackRef.current;
        const hasMeasuredScreenSignal =
          activeScreenTrack?.readyState === "live" &&
          activeScreenTrack.muted !== true &&
          screenLevel >= SHARED_AUDIO_SIGNAL_THRESHOLD;

        if (hasMeasuredScreenSignal) {
          const now = Date.now();
          if (sharedAudioSignalStartedAtRef.current === null) {
            sharedAudioSignalStartedAtRef.current = now;
          } else if (
            now - sharedAudioSignalStartedAtRef.current >=
            SHARED_AUDIO_SIGNAL_CONFIRM_MS
          ) {
            sharedAudioDetectedTrackRef.current = activeScreenTrack;
            setHasDetectedSharedAudio(true);
          }
        } else {
          sharedAudioSignalStartedAtRef.current = null;
        }
      }

      telehealthLevelAnimationRef.current = requestAnimationFrame(tick);
    };

    telehealthLevelAnimationRef.current = requestAnimationFrame(tick);
  };

  const stopVolumeMonitoring = (shouldResetLevel = true) => {
    isMonitoringRef.current = false;
    stopTelehealthLevelMonitoring(shouldResetLevel);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.disconnect();
      } catch (error) {
        console.error("Failed to disconnect source node", error);
        Sentry.captureException(error, {
          tags: { flow: "stopVolumeMonitoring" },
          extra: { patientId: patientID },
        });
      }
      sourceNodeRef.current = null;
    }

    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch (error) {
        console.error("Failed to disconnect analyser node", error);
        Sentry.captureException(error, {
          tags: { flow: "stopVolumeMonitoring" },
          extra: { patientId: patientID },
        });
      }
      analyserRef.current = null;
    }

    if (silenceGainNodeRef.current) {
      try {
        silenceGainNodeRef.current.disconnect();
      } catch (error) {
        console.error("Failed to disconnect gain node", error);
        Sentry.captureException(error, {
          tags: { flow: "stopVolumeMonitoring" },
          extra: { patientId: patientID },
        });
      }
      silenceGainNodeRef.current = null;
    }

    // ✅ DON'T close audioContext here - we keep it alive for the proxy
    // It will be closed in resetRecording() or component unmount

    dataArrayRef.current = null;

    if (shouldResetLevel) {
      setVolumeLevel(0);
    }
    setShowNoAudioWarning(false);
  };

  useEffect(() => {
    if (!isTelehealth || !audioManager) {
      return;
    }

    console.log("📡 Subscribing to audioManager state changes");

    const unsubscribe = audioManager.subscribe(async (state) => {
      console.log("🔔 AudioManager state changed:", state);
      setTelehealthAudioRevision((revision) => revision + 1);

      activeMicIdRef.current = state.activeMicrophoneId || "";
      setActiveMicId(state.activeMicrophoneId || "");
      if (state.preferredMicrophone && !preferredMicRef.current) {
        preferredMicRef.current = state.preferredMicrophone;
        setSelectedMicId(state.preferredMicrophone.deviceId || "");
      }
      setIsUsingFallbackMic(Boolean(state.isUsingFallback));

      if (state.hasMicrophone && recordingStatusRef.current === "recording") {
        await updateTelehealthAudioSource();
      }

      if (!state.hasScreenAudio && recordingStatusRef.current === "recording") {
        console.warn("⚠️ Screen audio stopped during recording");
        await updateTelehealthAudioSource();
        console.log("🔄 Switched to microphone-only mode");
      }

      if (state.hasMicrophone && recordingStatusRef.current === "inactive") {
        console.log("🔄 Microphone reconnected (not recording)");
        const combinedStream = audioManager.getCombinedStream();
        if (combinedStream) {
          streamRef.current = combinedStream;
          setStream(combinedStream);
          setPermission(true);
        }
      }
    });

    audioManagerUnsubscribeRef.current = unsubscribe;

    return () => {
      if (audioManagerUnsubscribeRef.current) {
        audioManagerUnsubscribeRef.current();
        audioManagerUnsubscribeRef.current = null;
        console.log("📡 Unsubscribed from audioManager");
      }
    };
  }, [isTelehealth, audioManager]);

  const reconcileRegularMicrophone = async (reason = "devicechange") => {
    if (isTelehealth || isUnmountedRef.current) {
      return false;
    }

    let microphones = [];
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      microphones = devices.filter((device) => device.kind === "audioinput");
      setAvailableMicrophones(microphones);
    } catch (error) {
      console.warn("Unable to enumerate microphones during recovery", error);
    }

    const preferredId = preferredMicRef.current?.deviceId || selectedMicIdRef.current || "";
    const activeTrack = streamRef.current?.getAudioTracks?.()[0];
    const activeId = activeTrack?.getSettings?.()?.deviceId || activeMicIdRef.current || "";
    const activeIsLive = activeTrack?.readyState === "live";
    const activeStillExists =
      activeIsLive &&
      (!activeId ||
        microphones.length === 0 ||
        microphones.some((device) => device.deviceId === activeId));
    const preferredDevice = preferredId
      ? microphones.find((device) => device.deviceId === preferredId)
      : null;

    if (preferredDevice && activeId !== preferredDevice.deviceId) {
      const restoredStream = await getMicrophonePermission(preferredDevice.deviceId, {
        updatePreference: false,
        allowFallback: false,
        reason: `${reason}-preferred-returned`,
      });
      if (restoredStream) {
        return true;
      }
    }

    if (activeStillExists) {
      return true;
    }

    const fallbackStream = await getMicrophonePermission(null, {
      updatePreference: false,
      allowFallback: true,
      reason: `${reason}-fallback`,
      useSelectedDevice: false,
    });
    return Boolean(fallbackStream);
  };

  // ===== REGULAR MODE DEVICE DETECTION =====
  useEffect(() => {
    if (isTelehealth) {
      return;
    }

    const handleDeviceChange = () => {
      console.log("🔌 Device change detected (regular mode)");

      const currentStatus = recordingStatusRef.current;
      if (currentStatus === "paused") {
        micDeviceChangedWhilePausedRef.current = true;
      }
      if (deviceChangeDebounceRef.current) {
        clearTimeout(deviceChangeDebounceRef.current);
      }
      deviceChangeDebounceRef.current = setTimeout(() => {
        deviceChangeDebounceRef.current = null;
        void reconcileRegularMicrophone("devicechange");
      }, DEVICE_CHANGE_DEBOUNCE_MS);
    };

    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
      deviceChangeListenerRef.current = handleDeviceChange;
      console.log("✅ Device change listener registered (regular mode)");
    }

    return () => {
      if (deviceChangeListenerRef.current && navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener("devicechange", deviceChangeListenerRef.current);
        deviceChangeListenerRef.current = null;
        console.log("🔌 Device change listener removed");
      }
      if (deviceChangeDebounceRef.current) {
        clearTimeout(deviceChangeDebounceRef.current);
        deviceChangeDebounceRef.current = null;
      }
    };
    // Reconciliation reads live refs; re-register only when switching recording modes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTelehealth]);

  const lastChunkTimeRef = useRef(0); // Track when last chunk was created
  const elapsedTimeRef = useRef(0); // Track actual elapsed time in a ref
  // Throttled tabs deliver setInterval ticks late, so elapsed time comes from
  // the monotonic clock rather than a tick count.
  const timerSegmentStartRef = useRef(null);
  const timerAccumulatedMsRef = useRef(0);
  const audioManagerUnsubscribeRef = useRef(null); // For telehealth audioManager subscription
  const deviceChangeListenerRef = useRef(null); // For regular mode devicechange listener
  const currentSourceStreamRef = useRef(null); // Track the current source stream for proxy
  const recordingDestinationRef = useRef(null); // Proxy destination for MediaRecorder
  const audioResilienceIntervalRef = useRef(null); // Watchdog that keeps the WebAudio graph alive
  const timerIntervalRef = useRef(null); // Timer interval reference
  const chunkIntervalRef = useRef(null); // Chunk interval reference

  /**
   * Audio-context resilience (telehealth patient-audio drop fix).
   *
   * Patient audio in a CVT/telehealth visit is captured from the shared tab via
   * getDisplayMedia and mixed with the provider mic through WebAudio
   * AudioContext(s) before being recorded. When the Knowtex tab/window is
   * minimized or otherwise backgrounded mid-visit, the browser can suspend that
   * WebAudio graph. The mic (getUserMedia) keeps recording, but the mixed
   * patient-audio path goes silent — so only the provider is captured, with no
   * error and the track still reporting "live".
   *
   * This effect defends the recorded stream while a recording is active by:
   *   1. auto-resuming any AudioContext the instant it reports "suspended"
   *      (statechange listener),
   *   2. polling as a backstop, because statechange events are unreliable on
   *      backgrounded tabs, and
   *   3. resuming immediately when the page becomes visible again.
   * It re-scans on each poll because the combined stream/context can be
   * hot-swapped mid-visit (mic switch or screen-share change).
   */
  useEffect(() => {
    const isActive = recordingStatus === "recording" || recordingStatus === "paused";
    if (!isActive) {
      return undefined;
    }

    const getContexts = () => {
      const candidates = [
        audioContextRef.current,
        latestAudioManagerRef.current?.audioContext,
        currentSourceStreamRef.current?._audioContext,
        streamRef.current?._audioContext,
      ];
      const unique = [];
      const seen = new Set();
      candidates.forEach((ctx) => {
        if (ctx && !seen.has(ctx) && typeof ctx.resume === "function") {
          seen.add(ctx);
          unique.push(ctx);
        }
      });
      return unique;
    };

    const resumeSuspended = () => {
      getContexts().forEach((ctx) => {
        if (ctx.state === "suspended") {
          ctx.resume().catch((err) => {
            console.warn("Failed to resume audio context (resilience):", err);
          });
        }
      });
      void latestAudioManagerRef.current?.ensureMixerRunning?.();
    };

    const handleStateChange = (event) => {
      const ctx = event?.target;
      if (ctx && ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }
    };

    const attached = new Set();
    const attach = () => {
      getContexts().forEach((ctx) => {
        if (!attached.has(ctx) && typeof ctx.addEventListener === "function") {
          attached.add(ctx);
          ctx.addEventListener("statechange", handleStateChange);
        }
      });
    };

    const handleVisibility = () => {
      if (!document.hidden) {
        resumeSuspended();
      }
    };

    attach();
    resumeSuspended();
    document.addEventListener("visibilitychange", handleVisibility);

    audioResilienceIntervalRef.current = window.setInterval(() => {
      attach();
      resumeSuspended();
    }, 1000);

    return () => {
      if (audioResilienceIntervalRef.current) {
        window.clearInterval(audioResilienceIntervalRef.current);
        audioResilienceIntervalRef.current = null;
      }
      document.removeEventListener("visibilitychange", handleVisibility);
      attached.forEach((ctx) => {
        try {
          ctx.removeEventListener("statechange", handleStateChange);
        } catch (e) {
          /* noop */
        }
      });
    };
  }, [recordingStatus]);

  const setLiveRecordingStatus = (status) => {
    recordingStatusRef.current = status;
    setRecordingStatus(status);
  };

  const stopCaptureWatchdogs = useCallback(() => {
    if (firstChunkDeadlineTimeoutRef.current) {
      clearTimeout(firstChunkDeadlineTimeoutRef.current);
      firstChunkDeadlineTimeoutRef.current = null;
    }
  }, []);

  const cancelUploadSettlePoll = useCallback(() => {
    uploadSettleGenerationRef.current += 1;
    if (uploadSettleTimerRef.current != null) {
      window.clearTimeout(uploadSettleTimerRef.current);
      uploadSettleTimerRef.current = null;
    }
  }, []);

  const syncUploadAvailability = useCallback(() => {
    if (!uploadActionsVisibleRef.current) {
      return;
    }
    setDisableUpload(
      shouldDisableUploadAudio({
        successfulChunkUploads: successfulUploadCountRef.current,
        inFlightChunkUploads: inFlightChunkUploadsRef.current,
      })
    );
  }, []);

  const settleChunkUpload = useCallback(() => {
    inFlightChunkUploadsRef.current = Math.max(0, inFlightChunkUploadsRef.current - 1);
    syncUploadAvailability();
  }, [syncUploadAvailability]);

  const startElapsedTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      return;
    }

    timerSegmentStartRef.current = performance.now();
    const timer = setInterval(() => {
      const elapsedMs =
        timerAccumulatedMsRef.current + (performance.now() - timerSegmentStartRef.current);
      const newTime = Math.floor(elapsedMs / 1000);
      elapsedTimeRef.current = newTime;
      setElapsedTime(newTime);
    }, 1000);
    timerIntervalRef.current = timer;
  }, []);

  const stopElapsedTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
      if (timerSegmentStartRef.current != null) {
        timerAccumulatedMsRef.current += performance.now() - timerSegmentStartRef.current;
        timerSegmentStartRef.current = null;
      }
    }
  }, []);

  const stopChunkChecker = useCallback(() => {
    if (chunkIntervalRef.current) {
      clearInterval(chunkIntervalRef.current);
      chunkIntervalRef.current = null;
    }
  }, []);

  const pauseForEmptyFirstChunk = useCallback((reason, firstChunkBytes) => {
    logWarn("Recording paused: empty first chunk", "recording.empty_first_chunk", {
      patient_id: patientID,
      conversation_id: conversationIDRef.current,
      is_telehealth: isTelehealth,
      reason,
      first_chunk_bytes: firstChunkBytes,
      elapsed_seconds: elapsedTimeRef.current,
      recorder_state: mediaRecorder.current?.state,
      audio_context_state: audioContextRef.current?.state,
      document_hidden: document.hidden,
    });

    if (mediaRecorder.current?.state === "recording") {
      mediaRecorder.current.pause();
      setLiveRecordingStatus("paused");
      stopElapsedTimer();
      stopChunkChecker();
    }
    stopCaptureWatchdogs();
    setShowRecordingErrorModal(true);
  }, [isTelehealth, patientID, stopCaptureWatchdogs, stopChunkChecker, stopElapsedTimer]);

  // Armed by the chunk checker right after it issues the first stop(). Do not
  // call requestData() early instead: with WebM/Opus only the first blob of a
  // MediaRecorder session carries the header, and each chunk is uploaded as
  // its own file, so that first stop() is the complete first file. Arming on
  // the cut (not at record start) means a throttled tab that cuts late is never
  // mistaken for a dead encoder; only a stop() that yields nothing is.
  const armFirstChunkDeadline = useCallback(() => {
    stopCaptureWatchdogs();

    firstChunkDeadlineTimeoutRef.current = setTimeout(() => {
      if (
        !shouldFailFirstChunkDeadline({
          hasAcceptedChunk: hasAcceptedChunkRef.current,
          secondsSinceFirstCut: FIRST_CHUNK_GRACE_SECONDS,
          recordingStatus: recordingStatusRef.current,
        })
      ) {
        return;
      }

      pauseForEmptyFirstChunk("deadline");
    }, FIRST_CHUNK_GRACE_SECONDS * 1000);
  }, [pauseForEmptyFirstChunk, stopCaptureWatchdogs]);

  const startChunkChecker = useCallback(() => {
    if (isMockPatient || chunkIntervalRef.current) {
      return;
    }

    const chunkChecker = setInterval(() => {
      if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
        const timeSinceLastChunk = elapsedTimeRef.current - lastChunkTimeRef.current;
        if (timeSinceLastChunk >= CHUNK_INTERVAL_SECONDS) {
          mediaRecorder.current.stop();
          mediaRecorder.current.start();
          lastChunkTimeRef.current = elapsedTimeRef.current;
          console.log(`✅ Chunk created at ${elapsedTimeRef.current}s recording time`);
          if (!hasAcceptedChunkRef.current) {
            firstCutIssuedRef.current = true;
            armFirstChunkDeadline();
          }
        }
      }
    }, 1000);
    chunkIntervalRef.current = chunkChecker;
  }, [armFirstChunkDeadline, isMockPatient]);

  const hasDeadRegularAudioTrack = (candidateStream) => {
    if (!candidateStream || typeof candidateStream.getAudioTracks !== "function") {
      return false;
    }

    const tracks = candidateStream.getAudioTracks();
    if (!Array.isArray(tracks) || tracks.length === 0) {
      return true;
    }

    return tracks.some((track) => track.readyState === "ended");
  };

  const recoverRegularMicrophoneSource = async (reason = "unknown") => {
    if (isTelehealth) {
      return true;
    }
    const recovered = await reconcileRegularMicrophone(reason);
    if (recovered && recordingStatusRef.current === "recording" && streamRef.current) {
      await startVolumeMonitoring(streamRef.current);
    }
    micDeviceChangedWhilePausedRef.current = false;
    return recovered;
  };

  useEffect(() => {
    if (recordingStatus !== "recording") {
      return undefined;
    }

    const ensureRecordingIntervals = () => {
      if (!timerIntervalRef.current) {
        startElapsedTimer();
      }

      if (
        !isMockPatient &&
        mediaRecorder.current?.state === "recording" &&
        !chunkIntervalRef.current
      ) {
        startChunkChecker();
      }
    };

    ensureRecordingIntervals();
    const watchdogId = window.setInterval(ensureRecordingIntervals, 1500);

    return () => {
      window.clearInterval(watchdogId);
    };
  }, [isMockPatient, recordingStatus, startChunkChecker, startElapsedTimer]);

  /**
   * Creates or resumes the shared Web Audio context before monitoring or
   * recording nodes are connected.
   */
  const ensureAudioContext = async () => {
    if (!audioContextRef.current) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        throw new Error("Web Audio API is not supported in this browser.");
      }
      audioContextRef.current = new AudioContextClass();
    }

    // Resume if suspended (critical for Safari/iOS and pre-recording previews)
    if (audioContextRef.current.state === "suspended") {
      await audioContextRef.current.resume();
    }
  };

  /**
   * Sets up the Web Audio proxy architecture (one-time setup)
   * Reuses the existing audioContextRef for both volume monitoring AND recording
   */
  const setupAudioProxy = async () => {
    await ensureAudioContext();

    // Create the destination node that MediaRecorder will listen to
    if (!recordingDestinationRef.current) {
      recordingDestinationRef.current = audioContextRef.current.createMediaStreamDestination();
      console.log("✅ Audio proxy destination created");
    }
  };

  /**
   * Connects an input stream to the proxy destination
   * This allows hot-swapping audio sources without stopping MediaRecorder
   */
  const connectAudioSource = async (inputStream) => {
    if (!inputStream) {
      console.error("❌ No input stream provided to connectAudioSource");
      return;
    }

    // Ensure proxy is set up
    await setupAudioProxy();

    console.log("🔌 Connecting audio source:", {
      trackCount: inputStream.getAudioTracks().length,
      tracks: inputStream.getAudioTracks().map((t) => ({
        id: t.id,
        label: t.label,
        enabled: t.enabled,
        readyState: t.readyState,
      })),
    });

    const previousSourceNode = sourceNodeRef.current;
    const previousSourceStream = currentSourceStreamRef.current;
    const nextSourceNode = audioContextRef.current.createMediaStreamSource(inputStream);

    // Connect to recording destination (for MediaRecorder)
    nextSourceNode.connect(recordingDestinationRef.current);

    // Also connect to analyser if it exists (for volume monitoring)
    if (analyserRef.current) {
      nextSourceNode.connect(analyserRef.current);
      console.log("🔊 Connected to volume analyser");
    }
    sourceNodeRef.current = nextSourceNode;
    currentSourceStreamRef.current = inputStream;

    if (previousSourceNode && previousSourceNode !== nextSourceNode) {
      try {
        previousSourceNode.disconnect();
        console.log("🔌 Disconnected old source node");
      } catch (e) {
        console.warn("Failed to disconnect old source node:", e);
      }
    }
    if (previousSourceStream && previousSourceStream !== inputStream) {
      previousSourceStream.getTracks().forEach((track) => {
        track.stop();
        console.log("⏹️ Stopped old track:", track.label);
      });
    }

    console.log("✅ Audio source connected to proxy");
  };

  /**
   * For telehealth: Updates the audio source when audioManager provides a new combined stream
   * Call this when the screen share changes or mic switches
   */
  const updateTelehealthAudioSource = async () => {
    if (!isTelehealth || !audioManager) {
      return;
    }

    console.log("🔄 Updating telehealth audio source...");

    const combinedStream = audioManager.getCombinedStream();
    if (!combinedStream) {
      console.warn("⚠️ No combined stream available from audioManager");
      return;
    }

    await connectAudioSource(combinedStream);

    // Update streamRef so other parts of the code still work
    streamRef.current = combinedStream;
    setStream(combinedStream);
    if (recordingStatusRef.current === "recording" || recordingStatusRef.current === "paused") {
      await startTelehealthLevelMonitoring();
    }

    console.log("✅ Telehealth audio source updated seamlessly");
  };

  const startVolumeMonitoring = async (mediaStream, telehealthLevelOptions) => {
    if (!mediaStream) {
      return;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;

    if (!AudioContextClass) {
      console.warn("Web Audio API is not supported in this browser.");
      return;
    }

    stopVolumeMonitoring();

    try {
      isMonitoringRef.current = true;

      // ✅ Set up the audio proxy (creates context + destination)
      await setupAudioProxy();

      // ✅ Connect the input stream to proxy
      await connectAudioSource(mediaStream);

      // ✅ Create analyser node
      const analyser = audioContextRef.current.createAnalyser();
      analyser.fftSize = 256;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // ✅ Connect source to analyser (already done in connectAudioSource)
      // But we need to reconnect to ensure analyser is in the chain
      if (sourceNodeRef.current) {
        sourceNodeRef.current.connect(analyser);
      }

      // Silent gain node to prevent audio playback
      const silenceGainNode = audioContextRef.current.createGain();
      silenceGainNode.gain.value = 0;
      analyser.connect(silenceGainNode);
      silenceGainNode.connect(audioContextRef.current.destination);

      await audioContextRef.current.resume().catch((resumeError) => {
        console.error("Failed to resume audio context", resumeError);
        Sentry.captureException(resumeError, {
          tags: { flow: "audioContextResume" },
          extra: { patientId: patientID },
        });
      });

      // Store refs
      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;
      silenceGainNodeRef.current = silenceGainNode;
      lastSoundDetectedRef.current = Date.now();
      setShowNoAudioWarning(false);
      await startTelehealthLevelMonitoring(telehealthLevelOptions);

      const updateVolume = () => {
        if (!isMonitoringRef.current || !analyserRef.current || !dataArrayRef.current) {
          return;
        }

        const normalized = getRmsNormalizedLevel(analyserRef.current, dataArrayRef.current);

        if (!isMonitoringRef.current) {
          return;
        }

        setVolumeLevel((prev) => {
          return prev * 0.7 + normalized * 0.3;
        });

        const now = Date.now();
        if (
          shouldShowSilenceWarning({
            combinedLevel: normalized,
            telehealthMicLevel: telehealthMicInstantLevelRef.current,
            isTelehealth,
            silenceMs: now - lastSoundDetectedRef.current,
            recordingStatus: recordingStatusRef.current,
          })
        ) {
          setShowNoAudioWarning(true);
        } else if (
          normalized > VOLUME_SILENCE_THRESHOLD ||
          (isTelehealth && telehealthMicInstantLevelRef.current > VOLUME_SILENCE_THRESHOLD)
        ) {
          lastSoundDetectedRef.current = now;
          setShowNoAudioWarning(false);
        }

        if (!isMonitoringRef.current) {
          return;
        }

        animationFrameRef.current = requestAnimationFrame(updateVolume);
      };

      animationFrameRef.current = requestAnimationFrame(updateVolume);
    } catch (error) {
      isMonitoringRef.current = false;
      console.error("Unable to start volume monitoring", error);
      Sentry.captureException(error, {
        tags: { flow: "startVolumeMonitoring" },
        extra: { patientId: patientID },
      });
    }
  };

  const clearAudioTestCountdown = () => {
    if (audioTestTimeoutRef.current) {
      clearTimeout(audioTestTimeoutRef.current);
      audioTestTimeoutRef.current = null;
    }
  };

  const stopAudioTest = () => {
    clearAudioTestCountdown();
    audioTestRefreshGenerationRef.current += 1;
    audioTestActiveRef.current = false;
    audioTestStartingRef.current = false;
    setIsAudioTestActive(false);
    setIsAudioTestStarting(false);
    if (recordingStatusRef.current === "inactive") {
      stopVolumeMonitoring();
    }
  };

  const startAudioTestCountdown = () => {
    clearAudioTestCountdown();

    audioTestTimeoutRef.current = setTimeout(() => {
      audioTestTimeoutRef.current = null;
      stopAudioTest();
    }, AUDIO_TEST_DURATION_MS);
  };

  const startAudioTest = async () => {
    if (
      !canStartAudioTest({
        isAudioTestStarting,
        recordingStatus: recordingStatusRef.current,
        hasRecorded,
      })
    ) {
      return;
    }

    audioTestStartingRef.current = true;
    setIsAudioTestStarting(true);
    try {
      let testStream = null;
      if (isTelehealth && audioManager) {
        testStream = audioManager.getCombinedStream();
        if (!testStream) {
          const result = await audioManager.setupMicrophone(selectedMicIdRef.current || null);
          if (!result.success) {
            throw new Error(result.error || "Unable to connect the microphone.");
          }
          testStream = audioManager.getCombinedStream();
        }
        const managerState = audioManager.getState();
        activeMicIdRef.current = managerState.activeMicrophoneId || "";
        setActiveMicId(managerState.activeMicrophoneId || "");
      } else {
        testStream = await getMicrophonePermission(selectedMicIdRef.current || null, {
          updatePreference: false,
          allowFallback: true,
          reason: "audio-test",
        });
      }

      if (!testStream) {
        throw new Error("Unable to connect the microphone.");
      }

      audioTestActiveRef.current = true;
      setIsAudioTestActive(true);
      await startVolumeMonitoring(testStream, {
        monitorMicrophone: true,
        monitorScreen: Boolean(isTelehealth && hasTelehealthScreenAudio),
      });
      if (audioTestActiveRef.current) {
        startAudioTestCountdown();
      }
    } catch (error) {
      audioTestActiveRef.current = false;
      setIsAudioTestActive(false);
      console.error("Unable to start audio test", error);
      Sentry.captureException(error, {
        tags: { flow: "startAudioTest" },
        extra: { patientId: patientID, isTelehealth },
      });
      setAlertModal({
        title: "Test Audio unavailable",
        message: error.message || "Unable to preview audio levels. Please try again.",
      });
    } finally {
      audioTestStartingRef.current = false;
      setIsAudioTestStarting(false);
    }
  };

  useEffect(() => {
    if (
      !audioTestActiveRef.current ||
      audioTestStartingRef.current ||
      recordingStatusRef.current !== "inactive"
    ) {
      return undefined;
    }

    const refreshGeneration = ++audioTestRefreshGenerationRef.current;
    let cancelled = false;

    const refreshAudioTestSources = async () => {
      let testStream = null;

      if (isTelehealth) {
        if (!audioManager || !hasTelehealthMicrophone) {
          stopVolumeMonitoring();
          return;
        }
        testStream = audioManager.getCombinedStream();
      } else {
        testStream = await getMicrophonePermission(selectedMicIdRef.current || null, {
          updatePreference: false,
          allowFallback: true,
          reason: "audio-test-source-refresh",
        });
      }

      const recordingInputTrack = testStream?.getAudioTracks?.()[0];
      if (
        cancelled ||
        refreshGeneration !== audioTestRefreshGenerationRef.current ||
        !audioTestActiveRef.current ||
        !recordingInputTrack ||
        recordingInputTrack.readyState !== "live"
      ) {
        return;
      }

      await startVolumeMonitoring(testStream, {
        monitorMicrophone: true,
        monitorScreen: Boolean(isTelehealth && hasTelehealthScreenAudio),
      });
    };

    void refreshAudioTestSources();

    return () => {
      cancelled = true;
    };
    // The refresh intentionally follows source availability and active device
    // changes; live refs guard test start/stop and superseded async work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    audioManager,
    hasTelehealthMicrophone,
    hasTelehealthScreenAudio,
    isTelehealth,
    telehealthAudioRevision,
  ]);

  const startRecording = async () => {
    if (
      isStartingRecording ||
      isAudioTestStarting ||
      isAudioTestActive ||
      audioTestActiveRef.current ||
      recordingStatusRef.current !== "inactive"
    ) {
      return;
    }
    if (!ensureSessionTimeLeft(RECORDING_MIN_SESSION_MS, "recording")) return;
    stopAudioTest();

    // Request the optional always-on-top Chrome window before any async setup
    // consumes the user activation from the Start Recording click.
    if (hasFloatingRecordingPopup) {
      void openFloatingRecordingIndicator();
    }
    setIsStartingRecording(true);
    try {
      setIsUploaded(false);
      setIsLoading(false);
      setIsAudio(true);

      // For mock patients, only start the timer - skip all audio recording logic
      if (isMockPatient) {
        console.log("🎭 [MOCK MODE] Starting recording - timer only, no audio processing");
        setLiveRecordingStatus("recording");
        micDeviceChangedWhilePausedRef.current = false;
        resumeRecoveryInProgressRef.current = false;
        lastSoundDetectedRef.current = Date.now();
        setShowNoAudioWarning(false);
        chunkNumber = 0;
        successfulUploadCountRef.current = 0;
        consecutiveInvalidChunksRef.current = 0;
        invalidAudioIssueHandledRef.current = false;
        hasAcceptedChunkRef.current = false;
        firstCutIssuedRef.current = false;

        stopElapsedTimer();
        stopChunkChecker();
        stopCaptureWatchdogs();
        startElapsedTimer();

        // No conversation ID, no MediaRecorder, no S3 uploads for mock patients
        return;
      }

      // ✅ Get fresh stream for telehealth or regular mic
      if (isTelehealth && audioManager) {
        console.log("🎥 Telehealth mode: Getting combined stream...");

        // Let audioManager prepare the combined stream
        await getMicrophonePermission();

        if (!streamRef.current) {
          alert("Please ensure both microphone and screen audio are connected before recording.");
          setIsAudio(false);
          return;
        }
      } else {
        console.log("🎤 Regular mic mode");
        const recordingStream = await getMicrophonePermission(selectedMicId || null, {
          updatePreference: false,
          allowFallback: true,
          reason: "recording-start",
        });
        if (!recordingStream) {
          setIsAudio(false);
          return;
        }
      }

      setLiveRecordingStatus("recording");
      micDeviceChangedWhilePausedRef.current = false;
      resumeRecoveryInProgressRef.current = false;
      lastSoundDetectedRef.current = Date.now();
      setShowNoAudioWarning(false);
      chunkNumber = 0;
      successfulUploadCountRef.current = 0;
      inFlightChunkUploadsRef.current = 0;
      consecutiveInvalidChunksRef.current = 0;
      invalidAudioIssueHandledRef.current = false;
      hasAcceptedChunkRef.current = false;
      firstCutIssuedRef.current = false;

      stopElapsedTimer();
      stopChunkChecker();
      stopCaptureWatchdogs();
      startElapsedTimer();

      try {
        // Inside the try so a failure resets the recording state it just set.
        const id = await createConversationID();
        conversationIDRef.current = id;
        setConversationID(id);

        // ✅ Set up the audio proxy and connect source
        await setupAudioProxy();
        await connectAudioSource(streamRef.current);

        // ✅ Start volume monitoring (which also sets up the analyser in the graph)
        startVolumeMonitoring(streamRef.current);

        // ✅ MediaRecorder listens to the STABLE proxy stream
        const stableProxyStream = recordingDestinationRef.current.stream;

        console.log("🎙️ Creating MediaRecorder from proxy stream:", {
          trackCount: stableProxyStream.getAudioTracks().length,
          tracks: stableProxyStream.getAudioTracks().map((t) => ({
            id: t.id,
            label: t.label,
            enabled: t.enabled,
            readyState: t.readyState,
          })),
        });

        // Validate proxy stream
        const tracks = stableProxyStream.getAudioTracks();
        if (!tracks || tracks.length === 0) {
          throw new Error("Proxy stream has no audio tracks");
        }

        const activeTracks = tracks.filter((track) => track.readyState === "live");
        if (activeTracks.length === 0) {
          throw new Error("Proxy stream has no active audio tracks");
        }

        // MediaRecorder options
        let options = {};

        const preferredMimeTypes = [
          "audio/webm;codecs=opus",
          "audio/webm",
          "audio/ogg;codecs=opus",
        ];

        for (const mimeType of preferredMimeTypes) {
          if (MediaRecorder.isTypeSupported(mimeType)) {
            options = {
              mimeType: mimeType,
              audioBitsPerSecond: 128000,
            };
            console.log(`✅ Using MIME type: ${mimeType}`);
            break;
          }
        }

        if (!options.mimeType) {
          console.warn("⚠️ No preferred format supported, using browser default");
        }

        // ✅ Create MediaRecorder from PROXY stream (not original stream)
        mediaRecorder.current = new MediaRecorder(stableProxyStream, options);

        // Set error handler BEFORE starting
        mediaRecorder.current.onerror = (event) => {
          console.error("❌ MediaRecorder error:", event.error);
          Sentry.captureException(event.error, {
            tags: { flow: "MediaRecorderOnError" },
            extra: { patientId: patientID },
          });

          sendServerLogging({
            body: {
              ...userJSON(localStorage.getItem("userName")),
              event: "ERROR_START_RECORDING",
              eventData: {
                error_message: event.error?.message || "Unknown MediaRecorder error",
                error_name: event.error?.name,
                isTelehealth: isTelehealth,
                state: mediaRecorder.current?.state,
              },
              patientId: patientID,
            },
          });

          setLiveRecordingStatus("inactive");
          setIsAudio(false);
          stopCaptureWatchdogs();
          alert(
            `Recording error: ${
              event.error?.message || "Failed to record audio"
            }. Please try again.`
          );
        };

        // Set data handler BEFORE starting (skip for mock patients)
        if (!isMockPatient) {
          mediaRecorder.current.ondataavailable = (e) => {
            if (chunkNumber < 240) {
              handleDataAvailable(e);
            } else {
              mediaRecorder.current.stop();
              mediaRecorder.current.stream.getTracks().forEach((track) => {
                track.stop();
              });
              alert(
                "You have exceeded the maximum time limit allowed for an audio recording. Please upload or delete this audio recording."
              );
              uploadActionsVisibleRef.current = true;
              setReadyToUpload(true);
              syncUploadAvailability();
              setLiveRecordingStatus("inactive");
              stopCaptureWatchdogs();
            }
          };
        }

        // Small delay to ensure everything is ready
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Verify MediaRecorder state before starting
        if (mediaRecorder.current.state !== "inactive") {
          throw new Error(`MediaRecorder in invalid state: ${mediaRecorder.current.state}`);
        }

        // NOW start recording
        try {
          mediaRecorder.current.start();
          console.log("✅ MediaRecorder started successfully");
          if (isTelehealth) {
            void persistTelehealthSuggestedMacros();
          }
        } catch (startError) {
          console.error("❌ Failed to start MediaRecorder:", startError);
          Sentry.captureException(startError, {
            tags: { flow: "startMediaRecorder" },
            extra: { patientId: patientID },
          });

          sendServerLogging({
            body: {
              ...userJSON(localStorage.getItem("userName")),
              event: "ERROR_START_RECORDING",
              eventData: {
                error_message: startError.message,
                error_name: startError.name,
                isTelehealth: isTelehealth,
                stream_tracks: tracks.length,
                active_tracks: activeTracks.length,
              },
              patientId: patientID,
            },
          });

          setLiveRecordingStatus("inactive");
          setIsAudio(false);
          stopCaptureWatchdogs();
          throw startError;
        }

        logInfo("Recording started", "recording.started", {
          patient_id: patientID,
          conversation_id: conversationIDRef.current ?? conversationID,
          mime_type: mediaRecorder.current.mimeType || options.mimeType,
          is_telehealth: isTelehealth,
          is_mock: false,
        });
        const releaseStaleHold = takeSessionHold();
        releaseSessionHoldRef.current = holdSessionForRecording();
        releaseStaleHold();

        startChunkChecker();
      } catch (err) {
        takeSessionHold()();
        console.error("❌ Error in startRecording:", err);
        Sentry.captureException(err, {
          tags: { flow: "startRecording" },
          extra: { patientId: patientID },
        });

        stopElapsedTimer();
        timerAccumulatedMsRef.current = 0;
        elapsedTimeRef.current = 0;
        lastChunkTimeRef.current = 0;
        setElapsedTime(0);
        stopChunkChecker();
        stopCaptureWatchdogs();
        setLiveRecordingStatus("inactive");
        setIsAudio(false);

        sendServerLogging({
          body: {
            ...userJSON(localStorage.getItem("userName")),
            event: "ERROR_START_RECORDING",
            eventData: {
              error_message: err.message.toString(),
              error_name: err.name,
              isTelehealth: isTelehealth,
            },
            patientId: patientID,
          },
        });

        alert(`Failed to start recording: ${err.message}. Please refresh the page and try again.`);
      }
    } finally {
      setIsStartingRecording(false);
    }
  };

  const handleDataAvailable = (e) => {
    // Skip audio processing for mock patients
    if (isMockPatient) {
      return;
    }

    const chunkDisposition = classifyAudioChunk({
      chunkNumber,
      sizeBytes: e.data.size,
    });

    if (chunkDisposition.action === "skip-empty") {
      // Empty blobs are skipped. The first-chunk deadline still catches a dead
      // encoder that never produces an accepted chunk.
      console.error(`❌ Chunk ${chunkNumber} has no data!`);
      return;
    }

    if (e.data.size > 0) {
      // ✅ For first chunk, analyze the actual audio data
      if (chunkNumber === 0) {
        analyzeAudioChunk(e.data, isTelehealth);
      }

      handleAudioFile(e.data);
    }
  };

  // ✅ Add this NEW function to deeply analyze audio data
  const analyzeAudioChunk = async (blob, isTelehealthMode) => {
    try {
      // Read file header to check actual format
      const arrayBuffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);

      // Get file signature (first 32 bytes)
      const signature = Array.from(bytes.slice(0, 32))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join(" ");

      // Check for common audio format signatures
      const isWebM = signature.includes("1a 45 df a3"); // WebM/Matroska signature
      const isOgg = signature.includes("4f 67 67 53"); // OGG signature
      const isMP3 = signature.includes("ff fb") || signature.includes("ff f3"); // MP3 frame sync
      const isRIFF = signature.includes("52 49 46 46"); // WAV/RIFF

      // Also test if it can be played back
      const audioURL = URL.createObjectURL(blob);
      const testAudio = new Audio(audioURL);

      let playbackSuccess = false;
      await new Promise((resolve) => {
        testAudio.addEventListener("loadedmetadata", () => {
          playbackSuccess = true;
          resolve();
        });

        testAudio.addEventListener("error", (err) => {
          console.error("❌ Playback test failed:", {
            mode: isTelehealthMode ? "TELEHEALTH" : "REGULAR",
            error: err,
          });
          resolve();
        });

        setTimeout(resolve, 2000);
      });

      URL.revokeObjectURL(audioURL);
    } catch (err) {
      console.error("❌ Error analyzing chunk:", err);
      Sentry.captureException(err, {
        tags: { flow: "analyzeAudioChunk" },
        extra: { patientId: patientID },
      });
    }
  };

  const handleAudioFile = (file) => {
    const activeConversationID = conversationIDRef.current ?? conversationID;
    if (!activeConversationID) {
      console.error("Missing conversation ID for audio chunk upload");
      return;
    }

    const currentChunkNumber = chunkNumber;
    const uploadConversationID = activeConversationID;
    const fileName = `${activeConversationID}-chunk_${currentChunkNumber}.mp3`;
    const blob = new Blob([file], { type: mimeType });
    const fileObj = new File([blob], fileName, {
      type: mimeType,
    });

    const fileType = mimeType;

    const fSize = fileObj.size / 1000000; //calculate file size

    const chunkDisposition = classifyAudioChunk({
      chunkNumber,
      sizeBytes: fileObj.size,
    });

    // Check if first chunk is empty or extremely small - indicates recording error
    if (chunkDisposition.action === "empty-first-chunk-error") {
      console.error("Recording Error: First chunk is empty or too small", {
        fileName: fileObj.name,
        size: fileObj.size,
        chunkNumber: currentChunkNumber,
      });

      pauseForEmptyFirstChunk("tiny_first_chunk", fileObj.size);
      return;
    }

    // Log suspicious small files for debugging
    if (fileObj.size < 1000) {
      // Less than 1KB
      console.warn(`Suspicious small chunk detected:`, {
        fileName: fileObj.name,
        size: fileObj.size,
        sizeKB: (fileObj.size / 1024).toFixed(2),
        chunkNumber: currentChunkNumber,
        timestamp: new Date().toISOString(),
        conversationID: activeConversationID,
      });

      // Optionally skip uploading extremely small chunks (likely empty)
      if (chunkDisposition.action === "skip-invalid") {
        console.error("Skipping upload of extremely small chunk (< 150 bytes)");
        // Do not increment chunkNumber for skipped files
        consecutiveInvalidChunksRef.current += 1;

        if (
          shouldStopForInvalidChunks(consecutiveInvalidChunksRef.current) &&
          !invalidAudioIssueHandledRef.current
        ) {
          invalidAudioIssueHandledRef.current = true;
          handlePersistentAudioIssue();
        }

        return;
      }
    } else {
      // Log normal chunks for comparison
      console.log(`Normal chunk:`, {
        fileName: fileObj.name,
        size: fileObj.size,
        sizeKB: (fileObj.size / 1024).toFixed(2),
        chunkNumber: currentChunkNumber,
      });
    }

    consecutiveInvalidChunksRef.current = 0;
    invalidAudioIssueHandledRef.current = false;
    hasAcceptedChunkRef.current = true;
    stopCaptureWatchdogs();

    // Store valid chunk for playback/state updates
    chunksRef.current.push(file);

    // Increment chunkNumber immediately after validation to avoid blocking recording
    chunkNumber++;

    // Upload to S3 asynchronously - don't block recording for network issues
    inFlightChunkUploadsRef.current += 1;
    const uploadStartedAt = Date.now();
    axios
      .post(
        `${process.env.REACT_APP_API_URL}/sign_s3_realtime`,
        {
          fileName: fileObj.name,
          fileType: fileType,
        },
        {
          headers: { token: getSessionToken() },
        }
      )
      .then((response) => {
        var returnData = response.data.data.returnData;
        var signedRequest = returnData.signedRequest;
        // console.log("Received a signed request " + signedRequest);
        var options = {
          headers: {
            "Content-Type": fileType,
          },
        };
        /**
        We use a pre-signed URL to audio the audio which is passed to sign_s3 function
        along with the file and header which uploads the audio to s3 using a pre-signed
        URL.
        */
        axios
          .put(signedRequest, fileObj, options)
          .then((result) => {
            // File successfully uploaded to S3
            const currentConversationID = conversationIDRef.current ?? conversationID;
            if (uploadConversationID === currentConversationID) {
              successfulUploadCountRef.current += 1;
            }

            // this.setState({ audio: url });

            // this.setState({
            //   isUploading: false,
            //   isUploaded: true,
            // });

            //console.log("upload time end " + Date.now());

            let uploadTimeDiff = Date.now() - uploadStartedAt;
            let uploadTimeInSeconds = uploadTimeDiff / 1000; // Convert to seconds

            const decoded_token = jwt_decode(getSessionToken());
            const user_id = decoded_token["user"]["id"];

            const userEvent = {
              env: process.env.REACT_APP_API_URL.includes("localhost") ? "local" : "prod",
              service: "knowtex-webApp",
              event: {
                eventName: "audio_finished_uploading",
                userId: user_id,
                patientID: patientID,
                screenName: "Record Page",
                timestamp: new Date().toISOString(),
                uploadTime: uploadTimeInSeconds,
                fileSize: fSize,
              },
            };

            trackEvent(
              Events.AUDIO_UPLOAD_COMPLETED,
              {
                patient_id: patientID,
                upload_time_seconds: uploadTimeInSeconds,
                file_size: fSize,
              },
              { groups: { patient: patientID } }
            );
          })
          .catch((error) => {
            console.error("S3 upload failed for chunk:", fileObj.name, error);
            Sentry.captureException(error, {
              tags: { flow: "handleAudioFile", step: "s3PutFailed" },
              extra: {
                fileName: fileObj.name,
                chunkNumber: currentChunkNumber,
                patientId: patientID,
              },
            });
          })
          .finally(() => {
            settleChunkUpload();
          });
      })
      .catch((error) => {
        console.error("Failed to get S3 signed URL for chunk:", fileObj.name, error);
        Sentry.captureException(error, {
          tags: { flow: "handleAudioFile", step: "signS3Failed" },
          extra: { fileName: fileObj.name, chunkNumber: currentChunkNumber, patientId: patientID },
        });
        settleChunkUpload();
      });
  };

  const handlePersistentAudioIssue = () => {
    console.error(
      "Detected more than three consecutive invalid audio chunks. Stopping recording.",
      {
        consecutiveInvalidChunks: consecutiveInvalidChunksRef.current,
      }
    );

    if (mediaRecorder.current) {
      try {
        if (mediaRecorder.current.state === "recording") {
          mediaRecorder.current.pause();
          setLiveRecordingStatus("paused");
          stopCaptureWatchdogs();
        }
      } catch (error) {
        console.error("Failed to pause media recorder after audio issue", error);
        Sentry.captureException(error, {
          tags: { flow: "handlePersistentAudioIssue" },
          extra: { patientId: patientID },
        });
      }
    }

    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "AUDIO_RECORDING_STOPPED_DUE_TO_INVALID_CHUNKS",
        eventData: {
          consecutiveInvalidChunks: consecutiveInvalidChunksRef.current,
          patientId: patientID,
          isTelehealth: isTelehealth,
        },
        patientId: patientID,
      },
    });
    logWarn("Recording stopped due to invalid chunks", "recording.stopped_invalid_chunks", {
      patient_id: patientID,
      conversation_id: conversationIDRef.current ?? conversationID,
      is_telehealth: isTelehealth,
      consecutive_invalid_chunks: consecutiveInvalidChunksRef.current,
      chunks_recorded: chunkNumber,
      elapsed_seconds: elapsedTimeRef.current,
    });

    const hasValidChunks = chunkNumber > 0;

    if (hasValidChunks) {
      stopRecording();

      consecutiveInvalidChunksRef.current = 0;

      setAlertModal({
        title: "Audio Issue Detected - Recording Stopped",
        message:
          "We detected your audio stopped working during the recording. We've saved everything up to that point and stopped the recording. You can either upload the partial recording or delete and start over.",
      });
      return;
    }

    resetRecording();
    takeSessionHold()();
    consecutiveInvalidChunksRef.current = 0;
    setShowRecordingErrorModal(true);
  };

  const changeStatusToProcessing = async (activeConversationID) => {
    const status = "Processing Audio";
    const body = { status };
    const MAX_ATTEMPTS = 3;
    const RETRY_DELAY_MS = 2000;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      let response;
      try {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("token", getSessionToken());

        response = await fetch(`${process.env.REACT_APP_API_URL}/notes/status/${patientID}`, {
          method: "PUT",
          headers: myHeaders,
          body: JSON.stringify(body),
        });
      } catch (error) {
        // A TypeError from fetch means the request never completed (dropped
        // connection, proxy blip); the server saw nothing, so it's safe to retry.
        if (error.name === "TypeError" && attempt < MAX_ATTEMPTS) {
          Sentry.addBreadcrumb({
            category: "changeStatusToProcessing",
            message: `Network error — retrying (${attempt}/${MAX_ATTEMPTS})`,
            level: "warning",
            data: { patientId: patientID, error: error.message },
          });
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          continue;
        }

        // Callers capture to Sentry; attach the attempt details for them.
        const wrappedError = new Error(
          `Failed to set status to Processing Audio after ${attempt} attempt(s): ${error.message}`
        );
        wrappedError.cause = error;
        wrappedError.attempts = attempt;
        throw wrappedError;
      }

      if (response.ok) {
        break;
      }

      if (response.status >= 500 && attempt < MAX_ATTEMPTS) {
        Sentry.addBreadcrumb({
          category: "changeStatusToProcessing",
          message: `HTTP ${response.status} — retrying (${attempt}/${MAX_ATTEMPTS})`,
          level: "warning",
          data: { patientId: patientID },
        });
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        continue;
      }

      const errText = await response.text().catch(() => "");
      const httpError = new Error(
        `Failed to set status to Processing Audio (${response.status}) after ${attempt} attempt(s)${
          errText ? `: ${errText}` : ""
        }`
      );
      httpError.attempts = attempt;
      httpError.httpStatus = response.status;
      throw httpError;
    }

    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "PROCESSING_STATUS_CHANGE",
        eventData: { mrn: mrn, isTelehealth: isTelehealth },
        patientId: patientID,
      },
    });
    if (!isMockPatient) {
      logInfo("Recording status changed to processing", "recording.status_changed_to_processing", {
        patient_id: patientID,
        conversation_id: activeConversationID,
        is_telehealth: isTelehealth,
        previous_status: patientStatus,
        current_status: "processing",
      });
    }
  };

  const appendDiarizedTranscriptCsvUrl = async (conversationId, totalChunks) => {
    if (
      !conversationId ||
      typeof totalChunks !== "number" ||
      Number.isNaN(totalChunks) ||
      totalChunks < 1
    ) {
      return;
    }

    try {
      console.log("adding conv_id to db");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/diarized-transcript-csv-url/${patientID}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
          body: JSON.stringify({
            conversation_id: conversationId,
            total_chunks: totalChunks,
          }),
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const message = errorBody.error || `HTTP error! status: ${response.status}`;
        throw new Error(message);
      }
    } catch (err) {
      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "ERROR_APPEND_DIARIZED_TRANSCRIPT_CSV_URL",
          eventData: {
            error_message: err.message.toString(),
            conversation_id: conversationId,
            total_chunks: totalChunks,
          },
          patientId: patientID,
        },
      });

      console.error("Error appending diarized_transcript_csv_url:", err.message);
      Sentry.captureException(err, {
        tags: { flow: "appendDiarizedTranscriptCsvUrl" },
        extra: { patientId: patientID },
      });
    }
  };

  //{doctor_name}/{month}/{day}/{year}-{doctor_click_record_time}-{patient_id}-chunk_{chunk_id}.mp3
  const createConversationID = async () => {
    if (!doctorName) {
      throw new Error("Missing user name");
    }
    var uploadTimeStart = await getTimeStamp();
    var conversationID = doctorName.replace(/\s+/g, "") + "/" + uploadTimeStart + "-" + patientID;

    // ✅ Append -dual-channel for telehealth appointments
    // var conversationID = isTelehealth
    //   ? "dual-channel-" + baseConversationID
    //   : baseConversationID;

    return conversationID;
  };

  const getTimeStamp = async () => {
    var d = new Date();
    var options = {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    };
    var timeStamp = d.toLocaleDateString("en-US", options);
    var formattedTimeStamp = timeStamp.replace(/,/g, "-").replace(/\s+/g, ""); //removes comma and spaces and adds '-' between date and time
    return formattedTimeStamp;
  };

  //pause functionality
  const togglePauseRecording = async () => {
    // For mock patients, only handle timer pause/resume
    if (isMockPatient) {
      if (recordingStatus === "recording") {
        setLiveRecordingStatus("paused");
        stopElapsedTimer();
        stopCaptureWatchdogs();
      } else if (recordingStatus === "paused") {
        setLiveRecordingStatus("recording");
        lastSoundDetectedRef.current = Date.now();
        setShowNoAudioWarning(false);

        startElapsedTimer();
      }
      return;
    }

    if (mediaRecorder.current && recordingStatus === "recording") {
      mediaRecorder.current.pause();
      setLiveRecordingStatus("paused");

      stopElapsedTimer();
      stopChunkChecker();
      stopCaptureWatchdogs();
    } else if (mediaRecorder.current && recordingStatus === "paused") {
      const shouldRecoverBeforeResume = !isTelehealth;

      if (shouldRecoverBeforeResume && !resumeRecoveryInProgressRef.current) {
        resumeRecoveryInProgressRef.current = true;
        const recoveryReason = micDeviceChangedWhilePausedRef.current
          ? "resume-after-devicechange"
          : hasDeadRegularAudioTrack(streamRef.current)
            ? "resume-after-dead-track"
            : "resume-proactive-rebind";
        const recovered = await recoverRegularMicrophoneSource(recoveryReason);
        resumeRecoveryInProgressRef.current = false;
        if (!recovered) {
          alert(
            "Your microphone changed while recording was paused and could not be reconnected. Please select a microphone and try again."
          );
          return;
        }
      }

      mediaRecorder.current.resume();
      setLiveRecordingStatus("recording");
      lastSoundDetectedRef.current = Date.now();
      setShowNoAudioWarning(false);

      startElapsedTimer();
      startChunkChecker();
      // Pausing clears the grace timer; if the first cut already happened and
      // still produced no accepted chunk, give it the grace period again.
      if (firstCutIssuedRef.current && !hasAcceptedChunkRef.current) {
        armFirstChunkDeadline();
      }
    }
  };

  const handleShortRecording = () => {
    if (mediaRecorder.current) {
      setLiveRecordingStatus("paused");
      mediaRecorder.current.pause();
      stopCaptureWatchdogs();
    }
  };

  const scheduleReadyToUploadAfterStop = () => {
    const MIN_UI_DELAY_MS = 2000;
    const startedAt = Date.now();
    let showedActions = false;

    cancelUploadSettlePoll();
    const generation = uploadSettleGenerationRef.current;

    const tick = () => {
      if (generation !== uploadSettleGenerationRef.current) {
        return;
      }

      const pending = inFlightChunkUploadsRef.current;
      const elapsed = Date.now() - startedAt;
      const minDelayElapsed = elapsed >= MIN_UI_DELAY_MS;

      // Delete stays available after the short stop delay. Upload stays disabled
      // until every in-flight chunk has settled and at least one succeeded.
      if (minDelayElapsed && !showedActions) {
        showedActions = true;
        uploadActionsVisibleRef.current = true;
        setIsLoading(false);
        setReadyToUpload(true);
      }

      if (showedActions) {
        syncUploadAvailability();
      }

      if (
        !shouldKeepPollingChunkUploads({
          minDelayElapsed,
          inFlightChunkUploads: pending,
        })
      ) {
        uploadSettleTimerRef.current = null;
        return;
      }

      uploadSettleTimerRef.current = window.setTimeout(tick, 150);
    };

    tick();
  };

  const stopRecording = () => {
    setLiveRecordingStatus("inactive");
    stopCaptureWatchdogs();
    micDeviceChangedWhilePausedRef.current = false;
    resumeRecoveryInProgressRef.current = false;
    setHasRecorded(true);
    setIsLoading(true);

    // For mock patients, skip MediaRecorder stop logic
    if (isMockPatient) {
      stopElapsedTimer();
      stopChunkChecker();

      setTimeout(function () {
        setIsLoading(false);
        setReadyToUpload(true);
      }, 2000);
      return;
    }

    if (mediaRecorder.current) {
      mediaRecorder.current.onstop = handleStop;
      mediaRecorder.current.stop();
    } else {
      scheduleReadyToUploadAfterStop();
    }
    stopVolumeMonitoring();

    stopElapsedTimer();
    stopChunkChecker();
  };

  const handleStop = () => {
    chunksRef.current.forEach((chunk) => {
      const blob = new Blob([chunk], { type: "audio/mp3" });
      setAudioURLs((prevAudioURLs) => [...prevAudioURLs, URL.createObjectURL(blob)]);
    });
    chunksRef.current = [];

    // Read the live ref, not the `elapsedTime` state, which can be a stale 0 when handleStop runs via mediaRecorder.onstop from a recording-start closure.
    if (isRecordingTooShort(elapsedTimeRef.current)) {
      setAlertModal({
        title: "Recording Too Short",
        message:
          "Audio recording must be at least 2 seconds long to be uploaded. Please delete this recording and try again.",
      });
      setDisableUpload(true);
      setIsLoading(false);
      return;
    }

    setDisableUpload(true);
    scheduleReadyToUploadAfterStop();
  };

  async function uploadAudio() {
    if (inFlightChunkUploadsRef.current > 0) {
      setDisableUpload(true);
      return;
    }
    if (uploadAudioInFlightRef.current) {
      Sentry.addBreadcrumb({
        category: "uploadAudio",
        message: "Blocked by uploadAudioInFlightRef — upload already in progress",
        level: "warning",
      });
      return;
    }
    uploadAudioInFlightRef.current = true;
    // Captured before resetRecording() zeroes the ref so the catch block
    // reports the real count instead of 0.
    let totalChunks = successfulUploadCountRef.current;
    // Taken when the recorder is reset: from then on this upload is the recording's last
    // step, so its session hold is released when the upload ends, success or failure.
    let releaseSessionHold = null;

    try {
      setIsUploading(true);
      setIsLoading(true);

      // For mock patients, just update status to "Processing Audio" and show success UI
      // Note and status will be updated when user clicks "Return Home"
      if (isMockPatient) {
        try {
          // Reset recording state immediately (like normal flow)
          resetRecording();
          releaseSessionHold = takeSessionHold();

          // Update status to "Processing Audio" (like normal flow)
          await changeStatusToProcessing();

          // Update UI for success (matching normal upload flow)
          setIsLoading(false);
          setIsUploading(false);
          setIsUploaded(true);

          // Server logging
          sendServerLogging({
            body: {
              ...userJSON(localStorage.getItem("userName")),
              event: "MOCK_AUDIO_UPLOADED",
              eventData: { mrn: mrn, patientName: patientName },
              patientId: patientID,
            },
          });

          return { success: true };
        } catch (error) {
          Sentry.captureException(error, {
            tags: { logEvent: "ERROR_MOCK_UPLOADING" },
            extra: { patientId: patientID },
          });
          // Update UI state for error case
          setIsLoading(false);
          setIsUploading(false);

          // Server logging
          sendServerLogging({
            body: {
              ...userJSON(localStorage.getItem("userName")),
              event: "ERROR_MOCK_UPLOADING",
              eventData: { error_message: error.message.toString() },
              patientId: patientID,
            },
          });

          alert("Failed to update patient status. Please try again.");
        }
        return;
      }

      const activeConversationID = conversationIDRef.current ?? conversationID;
      totalChunks = successfulUploadCountRef.current;

      if (totalChunks < 1) {
        setIsLoading(false);
        setIsUploading(false);
        sendServerLogging({
          body: {
            ...userJSON(localStorage.getItem("userName")),
            event: "ERROR_UPLOADING",
            eventData: { error_message: "No valid audio chunks to upload." },
            patientId: patientID,
          },
        });
        logWarn("Recording upload skipped: no valid chunks", "recording.upload_failed_no_chunks", {
          patient_id: patientID,
          conversation_id: activeConversationID,
          is_telehealth: isTelehealth,
          elapsed_seconds: elapsedTimeRef.current,
          chunk_count: totalChunks,
          chunks_recorded: chunkNumber,
        });
        toast.error("No audio was captured, so nothing was submitted. Please record again.");
        return;
      }

      const recordedSeconds = elapsedTimeRef.current;

      releaseSessionHold = takeSessionHold();
      try {
        resetRecording();
      } catch (resetErr) {
        Sentry.captureException(resetErr, {
          tags: { flow: "uploadAudio", step: "resetRecording" },
          extra: { conversation_id: activeConversationID, patientId: patientID },
        });
      }

      const endpointUrl = process.env.REACT_APP_MERGE_TRANSCRIPT_API_URL;
      if (!endpointUrl || !String(endpointUrl).trim()) {
        const err = new Error("Merge transcript URL is not configured.");
        Sentry.captureException(err, {
          tags: { flow: "uploadAudio", step: "missingMergeUrl" },
          extra: { patientId: patientID },
        });
        throw err;
      }

      Sentry.addBreadcrumb({
        category: "uploadAudio",
        message: "About to call postData (merge request)",
        level: "info",
        data: {
          conversation_id: activeConversationID,
          total_chunks: totalChunks,
          endpoint: endpointUrl,
        },
      });

      // The merge request is what actually kicks off note generation, so it
      // goes first: once it's accepted the recording is safe even if the
      // status write below fails.
      const data = await postData(
        endpointUrl,
        {
          conversation_id: activeConversationID,
          total_chunks: totalChunks,
        },
        "POST",
        MERGE_REQUEST_TIMEOUT_MS
      );

      Sentry.addBreadcrumb({
        category: "uploadAudio",
        message: "postData resolved — merge request succeeded",
        level: "info",
        data: { conversation_id: activeConversationID, total_chunks: totalChunks },
      });

      Sentry.addBreadcrumb({
        category: "uploadAudio",
        message: "About to call changeStatusToProcessing",
        level: "info",
        data: { conversation_id: activeConversationID, patientId: patientID },
      });

      try {
        await changeStatusToProcessing(activeConversationID);
      } catch (statusError) {
        // Processing is already underway; don't show the user a failure that
        // would tempt them to re-record.
        Sentry.captureException(statusError, {
          tags: {
            flow: "uploadAudio",
            step: "statusAfterMerge",
            attempts_made: String(statusError.attempts ?? ""),
          },
          extra: {
            conversation_id: activeConversationID,
            total_chunks: totalChunks,
            patientId: patientID,
            http_status: statusError.httpStatus,
          },
        });
      }

      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "AUDIO_UPLOADED",
          eventData: { mrn: mrn, isTelehealth: isTelehealth },
          patientId: patientID,
        },
      });
      logInfo("Conversation upload request succeeded", "recording.conversation_upload_succeeded", {
        patient_id: patientID,
        conversation_id: activeConversationID,
        is_telehealth: isTelehealth,
        chunk_count: totalChunks,
        duration_seconds: recordedSeconds,
      });

      setIsLoading(false);
      setIsUploading(false);
      setIsUploaded(true);

      return data;
    } catch (error) {
      setIsLoading(false);
      setIsUploading(false);

      Sentry.captureException(error, {
        tags: { flow: "uploadAudio", step: "catchBlock" },
        extra: {
          conversation_id: conversationIDRef.current ?? conversationID,
          total_chunks: totalChunks,
          patientId: patientID,
        },
      });

      console.error("Error in uploadAudio:", error);
      toast.error("Failed to submit your recording. Please contact support.");
    } finally {
      uploadAudioInFlightRef.current = false;
      releaseSessionHold?.();
    }
  }

  async function postData(
    url = "",
    data = {
      conversation_id: conversationIDRef.current ?? conversationID,
      total_chunks: successfulUploadCountRef.current,
    },
    method = "POST",
    timeoutMs = MERGE_REQUEST_TIMEOUT_MS
  ) {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 2000;
    const reqConversationId = data.conversation_id ?? conversationIDRef.current ?? conversationID;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      const controller = new AbortController();
      const timeoutHandle = setTimeout(() => controller.abort(), timeoutMs);

      try {
        const response = await fetch(url, {
          method: method,
          mode: "cors",
          cache: "no-cache",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
          signal: controller.signal,
          redirect: "follow",
          body: JSON.stringify(data),
        });

        clearTimeout(timeoutHandle);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json().catch(() => ({}));
      } catch (error) {
        clearTimeout(timeoutHandle);

        const isRetryable = error.name === "TypeError" && attempt < MAX_RETRIES;

        if (!isRetryable) {
          const wrappedError = new Error(
            `Conversation upload failed after ${attempt} attempt(s): ${error.message} [${reqConversationId}]`
          );
          wrappedError.cause = error;
          Sentry.captureException(wrappedError, {
            tags: { flow: "postData", step: "fetchFailed", attempts_made: String(attempt) },
            extra: { url, method, conversation_id: reqConversationId },
          });
          throw wrappedError;
        }

        Sentry.addBreadcrumb({
          category: "postData",
          message: `TypeError — retrying (${attempt}/${MAX_RETRIES})`,
          level: "warning",
          data: { url, conversation_id: reqConversationId },
        });

        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      }
    }
  }

  //deletes audio and resets back to original state
  const resetRecording = () => {
    clearAudioTestCountdown();
    audioTestRefreshGenerationRef.current += 1;
    audioTestActiveRef.current = false;
    audioTestStartingRef.current = false;
    setIsAudioTestActive(false);
    setIsAudioTestStarting(false);
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "DELETE_RECORDED_AUDIO",
        eventData: {},
        patientId: patientID,
      },
    });

    stopVolumeMonitoring();

    // ✅ CRITICAL: Properly cleanup MediaRecorder and all intervals
    if (mediaRecorder.current) {
      mediaRecorder.current.ondataavailable = null;
      mediaRecorder.current.onstop = null;
      mediaRecorder.current.onerror = null;

      if (mediaRecorder.current.state === "recording" || mediaRecorder.current.state === "paused") {
        try {
          mediaRecorder.current.stop();
        } catch (err) {
          console.error("Error stopping mediaRecorder:", err);
          Sentry.captureException(err, {
            tags: { flow: "resetRecording" },
            extra: { patientId: patientID },
          });
        }
      }

      if (mediaRecorder.current._audioContext) {
        mediaRecorder.current._audioContext.close().catch((err) => {
          console.error("Failed to close AudioContext:", err);
          Sentry.captureException(err, {
            tags: { flow: "resetRecording" },
            extra: { patientId: patientID },
          });
        });
      }

      if (mediaRecorder.current.stream) {
        mediaRecorder.current.stream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      mediaRecorder.current = null;
    }

    if (audioURLs.length > 0) {
      audioURLs.forEach((url) => URL.revokeObjectURL(url));
    }

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    if (chunkIntervalRef.current) {
      clearInterval(chunkIntervalRef.current);
      chunkIntervalRef.current = null;
    }

    // Reset the state and other variables.
    chunksRef.current = [];
    setAudio(null);
    setLiveRecordingStatus("inactive");
    stopCaptureWatchdogs();
    cancelUploadSettlePoll();
    uploadActionsVisibleRef.current = false;
    hasAcceptedChunkRef.current = false;
    firstCutIssuedRef.current = false;
    setAudioChunks([]);
    setAudioURLs([]);
    setHasRecorded(false);
    setIsAudio(false);
    setConversationID(null);
    setIsUploading(false);
    setElapsedTime(0);
    chunkNumber = 0;
    successfulUploadCountRef.current = 0;
    inFlightChunkUploadsRef.current = 0;
    setReadyToUpload(false);
    setIsLoading(false);
    setIsUploaded(false);
    conversationIDRef.current = null;
    consecutiveInvalidChunksRef.current = 0;
    invalidAudioIssueHandledRef.current = false;

    // ✅ CLEANUP: Reset refs
    elapsedTimeRef.current = 0;
    timerSegmentStartRef.current = null;
    timerAccumulatedMsRef.current = 0;
    lastChunkTimeRef.current = 0;

    // ✅ Cleanup telehealth FIRST (this will call parent's cleanup)
    if (isTelehealth && onTelehealthCleanup) {
      onTelehealthCleanup();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setStream(null);
    setPermission(false);

    if (recordingDestinationRef.current) {
      recordingDestinationRef.current = null;
    }

    if (currentSourceStreamRef.current) {
      currentSourceStreamRef.current.getTracks().forEach((track) => track.stop());
      currentSourceStreamRef.current = null;
    }

    // NOW close the audioContext
    if (audioContextRef.current) {
      audioContextRef.current.close().catch((err) => {
        console.error("Failed to close audio context", err);
        Sentry.captureException(err, {
          tags: { flow: "resetRecording" },
          extra: { patientId: patientID },
        });
      });
      audioContextRef.current = null;
    }
    // ✅ Re-acquire microphone after cleanup so the page is ready to record again.
    // The OS/browser may not release the capture device immediately after the
    // tracks above are stopped, which intermittently causes
    // "NotReadableError: Could not start audio source" — so retry with backoff.
    reacquireMicrophoneAfterReset();
  };

  const MIC_REACQUIRE_DELAYS_MS = [300, 1000, 2500, 5000];

  const reacquireMicrophoneAfterReset = (attempt = 0) => {
    if (!("MediaRecorder" in window) || isUnmountedRef.current) {
      return;
    }

    if (micReacquireTimeoutRef.current) {
      clearTimeout(micReacquireTimeoutRef.current);
    }

    micReacquireTimeoutRef.current = setTimeout(() => {
      micReacquireTimeoutRef.current = null;

      navigator.mediaDevices
        .getUserMedia({
          audio: true,
          video: false,
        })
        .then((mediaStream) => {
          // Component unmounted while getUserMedia was pending — release the
          // stream, otherwise the browser mic indicator stays on forever.
          if (isUnmountedRef.current) {
            mediaStream.getTracks().forEach((track) => track.stop());
            return;
          }
          if (streamRef.current && streamRef.current !== mediaStream) {
            streamRef.current.getTracks().forEach((track) => track.stop());
          }
          streamRef.current = mediaStream;
          setPermission(true);
          setStream(mediaStream);
        })
        .catch((err) => {
          console.error(`❌ Failed to get microphone after reset (attempt ${attempt + 1}):`, err);

          // Retry transient failures (e.g. NotReadableError while the OS is
          // still releasing the device). Don't retry an explicit denial.
          const isDenied = err?.name === "NotAllowedError" || err?.name === "SecurityError";
          if (
            !isDenied &&
            !isUnmountedRef.current &&
            attempt + 1 < MIC_REACQUIRE_DELAYS_MS.length
          ) {
            reacquireMicrophoneAfterReset(attempt + 1);
            return;
          }

          Sentry.captureException(err, {
            tags: { flow: "resetRecording" },
            extra: { patientId: patientID, reacquireAttempts: attempt + 1 },
          });
          if (!isUnmountedRef.current) {
            setPermission(false);
            setStream(null);
          }
        });
    }, MIC_REACQUIRE_DELAYS_MS[attempt]);
  };

  // Format seconds into mm:ss (or hh:mm:ss past an hour) for the timer display.
  const formatTime = formatRecordingTime;

  useEffect(() => {
    if (isUploaded) {
      setShowUploadBanner(true);
      // Hide the banner after 5 seconds, optional
      const timer = setTimeout(() => {
        setShowUploadBanner(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isUploaded]);

  useEffect(() => {
    return () => {
      takeSessionHold()();
      stopCaptureWatchdogs();
      cancelUploadSettlePoll();
      uploadActionsVisibleRef.current = false;
      // Stop volume monitoring
      clearAudioTestCountdown();
      audioTestRefreshGenerationRef.current += 1;
      audioTestActiveRef.current = false;
      audioTestStartingRef.current = false;
      stopVolumeMonitoring(false);

      // Stop and cleanup MediaRecorder
      if (mediaRecorder.current) {
        if (
          mediaRecorder.current.state === "recording" ||
          mediaRecorder.current.state === "paused"
        ) {
          try {
            mediaRecorder.current.stop();
          } catch (err) {
            console.error("Error stopping mediaRecorder on unmount:", err);
            Sentry.captureException(err, {
              tags: { flow: "unmount" },
              extra: { patientId: patientID },
            });
          }
        }

        // ✅ CRITICAL: Stop the stream before component unmounts
        if (mediaRecorder.current.stream) {
          mediaRecorder.current.stream.getTracks().forEach((track) => {
            track.stop();
          });
        }

        mediaRecorder.current = null;
      }

      // Stop streamRef tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        streamRef.current = null;
      }

      // ✅ CRITICAL FOR TELEHEALTH: Cleanup audio manager on unmount
      if (latestIsTelehealthRef.current && latestAudioManagerRef.current) {
        latestAudioManagerRef.current.cleanup();
      }
    };
    // This teardown must only run on a true component unmount. Telehealth
    // source changes are handled by the live audio-test refresh effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        height: "auto",
        minHeight: "100%",
        width: "100%",
        overflow: "visible",
        position: "relative",
      }}
    >
      {hasFloatingRecordingPopup ? (
        <RecordingPictureInPicture
          elapsedTime={elapsedTime}
          isStarting={isStartingRecording}
          onReturnToKnowtex={() => {
            window.focus();
            closeFloatingRecordingIndicator();
          }}
          pictureInPictureWindow={pictureInPictureWindow}
          recordingStatus={recordingStatus}
        />
      ) : null}
      {/* Left Pane - Note (slides up from bottom left) */}
      <div
        ref={noteAccordionRef}
        role="region"
        aria-label="Current note panel"
        className={`${css.noteAccordion} ${isNoteAccordionOpen ? css.noteAccordionOpen : ""}`}
        style={{
          position: "fixed",
          bottom: "80px",
          height: isNoteAccordionOpen ? "calc(100dvh - 80px)" : "0", // Leave room for button
          backgroundColor: "#fafafa",
          borderRight: isNoteAccordionOpen ? "2px solid #e5e7eb" : "none",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: "all 0.4s ease-in-out",
          zIndex: 20,
          boxShadow: isNoteAccordionOpen ? "4px 0 20px rgba(0, 0, 0, 0.1)" : "none",
        }}
      >
        {isNoteAccordionOpen ? (
          <RecordNotePanel
            activeTab={activeLeftPaneTab}
            onTabChange={setActiveLeftPaneTab}
            patientName={patientName}
            patientStatus={patientStatus}
            isNoteLoading={isNoteLoading}
            noteError={noteError}
            noteContent={noteContent}
            notePlaceholder={notePlaceholder}
            isCustomPromptLoading={isCustomPromptLoading}
            hasCustomPrompt={hasCustomPrompt}
            isCustomPromptExpanded={isCustomPromptExpanded}
            customPromptValue={customPromptValue}
            onToggleCustomPrompt={handleToggleCustomPrompt}
            isChartHistoryLoading={isChartHistoryLoading}
            chartHistoryError={chartHistoryError}
            chartHistoryFields={chartHistoryFields}
          />
        ) : null}
      </div>

      <RecorderStage
        isNoteAccordionOpen={isNoteAccordionOpen}
        showNoAudioWarning={showNoAudioWarning}
        showNoPatientAudioWarning={shouldShowNoPatientAudioWarning({
          isTelehealth,
          hasDetectedSharedAudio,
          recordingStatus,
          elapsedSeconds: elapsedTime,
        })}
        recordingStatus={recordingStatus}
        hasRecorded={hasRecorded}
        formattedTime={formatTime(elapsedTime)}
        permission={permission}
        onRequestMicrophone={getMicrophonePermission}
        microphoneSrc={mic}
        isTelehealth={isTelehealth}
        volumeLevel={volumeLevel}
        telehealthMicVolumeLevel={telehealthMicVolumeLevel}
        telehealthScreenVolumeLevel={telehealthScreenVolumeLevel}
        hasTelehealthScreenAudio={hasTelehealthScreenAudio}
        hasDetectedSharedAudio={hasDetectedSharedAudio}
        isAudioTestActive={isAudioTestActive}
        isAudioTestStarting={isAudioTestStarting}
        onToggleAudioTest={isAudioTestActive ? stopAudioTest : startAudioTest}
        availableMicrophones={availableMicrophones}
        selectedMicId={activeMicId || selectedMicId}
        onMicrophoneChange={(deviceId) => {
          void handleMicrophoneSelect({ target: { value: deviceId } });
        }}
        isMergingChunks={chunkNumber === 240}
        isStartingRecording={isStartingRecording}
        isAudio={isAudio}
        org={org}
        onStartRecording={startRecording}
        onReturnHome={handleReturnHomeClick}
        canStop={elapsedTime >= MINIMUM_STOP_SECONDS}
        onStopRecording={stopRecording}
        onTogglePause={togglePauseRecording}
        onRequestDelete={() => setShowDeleteAudioModal(true)}
        isLoading={isLoading}
        hasFloatingRecordingPopup={hasFloatingRecordingPopup}
        isFloatingIndicatorSupported={isFloatingRecordingIndicatorSupported}
        isFloatingIndicatorOpen={isFloatingRecordingIndicatorOpen}
        isFloatingIndicatorOpening={isFloatingRecordingIndicatorOpening}
        onShowFloatingIndicator={() => {
          void openFloatingRecordingIndicator();
        }}
        readyToUpload={readyToUpload}
        isUploadDisabled={diasbleUpload}
        onUpload={uploadAudio}
        showDeleteModal={showDeleteAudioModal}
        onCancelDelete={() => setShowDeleteAudioModal(false)}
        onConfirmDelete={() => {
          if (!isMockPatient) {
            logInfo("Recording discarded", "recording.discarded", {
              patient_id: patientID,
              conversation_id: conversationIDRef.current ?? conversationID,
              is_telehealth: isTelehealth,
              elapsed_seconds: elapsedTimeRef.current,
              chunks_uploaded: successfulUploadCountRef.current,
            });
          }
          resetRecording();
          takeSessionHold()();
          setShowDeleteAudioModal(false);
        }}
        showUploadBanner={showUploadBanner}
      />

      <RecordingErrorModal
        open={showRecordingErrorModal}
        onRefresh={() => {
          sendServerLogging({
            body: {
              ...userJSON(localStorage.getItem("userName")),
              event: "EMPTY_AUDIO_REFRESH_CLICKED",
              eventData: {
                error_message: "First chunk was 0B or 110B",
                isTelehealth: isTelehealth,
              },
              patientId: patientID,
            },
          });
          setShowRecordingErrorModal(false);
          window.location.reload();
        }}
        onContactSupport={() => {
          sendServerLogging({
            body: {
              ...userJSON(localStorage.getItem("userName")),
              event: "EMPTY_AUDIO_CONTACT_US_CLICKED",
              eventData: {
                error_message: "First chunk was 0B or 110B",
                isTelehealth: isTelehealth,
              },
              patientId: patientID,
            },
          });
          setShowRecordingErrorModal(false);
          openPylonChat();
        }}
      />

      {showNoteButton ? (
        <RecordNoteToggle
          className={`${css.noteAccordionToggle} fixed`}
          isOpen={isNoteAccordionOpen}
          isPrecharting={isPrecharting}
          wordCount={countWords(noteContent)}
          onToggle={handleToggleNoteAccordion}
        />
      ) : null}

      <ConfirmationModal
        open={showLeaveConfirmation}
        onCancel={cancelLeave}
        onContinue={confirmLeave}
        title="Recording in progress"
        continueText="Confirm"
        continueVariant="primary-danger"
        cancelText="Continue Recording"
      >
        Are you sure you want to abandon your recording?
      </ConfirmationModal>

      {alertModal && (
        <AlertModal
          title={alertModal.title}
          message={alertModal.message}
          onClose={() => setAlertModal(null)}
        />
      )}
    </div>
  );
};

export default AudioRecorder;
